/* eslint-disable react/no-unescaped-entities */
/* Bank-level top-right panels:
   - ImportBankPanel: bulk-import JSON/CSV → author-pending
   - ExportBankPanel: dump full bank (all states) as JSON or CSV for backup
   - RoundTripCheckPanel: diff a reference file vs the current bank
   - CourseGapPanel: fan-out per-module LO gap analysis across all modules */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle, CheckCircle2, Copy, Download, FileText, GitCompare, Headphones, Image as ImageIcon, ListChecks,
  Mail, RefreshCw, Share2, Sparkles, Upload, UserPlus, X,
} from "lucide-react";
import { adminApi, getAdminToken } from "../portal/shell";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/* --- CSV parser: tolerates quoted fields with commas & escaped quotes. --- */
function parseCsv(text) {
  const rows = [];
  let field = "", row = [], inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQ) {
      if (ch === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (ch === '"') { inQ = false; }
      else { field += ch; }
    } else {
      if (ch === '"') { inQ = true; }
      else if (ch === ",") { row.push(field); field = ""; }
      else if (ch === "\r") { /* skip */ }
      else if (ch === "\n") { row.push(field); rows.push(row); row = []; field = ""; }
      else { field += ch; }
    }
  }
  if (field.length || row.length) { row.push(field); rows.push(row); }
  if (!rows.length) return [];
  const header = rows[0].map((h) => h.trim());
  return rows.slice(1).filter((r) => r.length > 1 || (r[0] && r[0].trim()))
    .map((r) => {
      const obj = {};
      header.forEach((h, idx) => { obj[h] = (r[idx] ?? "").trim(); });
      return obj;
    });
}

function normalizeItems(raw) {
  const arr = Array.isArray(raw) ? raw : Array.isArray(raw?.items) ? raw.items : null;
  if (!arr) return { items: null, error: "Expected a JSON array or {items:[...]}." };
  const items = [];
  for (let i = 0; i < arr.length; i++) {
    const r = arr[i];
    const options = Array.isArray(r.options) ? r.options
      : [r.option_a, r.option_b, r.option_c, r.option_d].filter((x) => x !== undefined && x !== "");
    const ci = parseInt(r.correct_index, 10);
    if (!r.module_slug || !r.prompt || options.length < 2 || Number.isNaN(ci)) {
      return { items: null, error: `Row ${i + 1}: missing module_slug/prompt/options/correct_index.` };
    }
    items.push({
      module_slug: String(r.module_slug),
      prompt: String(r.prompt),
      options: options.map((o) => String(o)),
      correct_index: ci,
      rationale: r.rationale ? String(r.rationale) : "",
      learning_objective_ref: r.learning_objective_ref ? String(r.learning_objective_ref) : null,
      scope: r.scope || "both",
      source_question_number: r.source_question_number != null && r.source_question_number !== ""
        ? parseInt(r.source_question_number, 10) : null,
    });
  }
  return { items, error: null };
}

export function ImportBankPanel({ courseId, course, onClose, onImported }) {
  const [text, setText] = useState("");
  const [parsed, setParsed] = useState(null);
  const [parseErr, setParseErr] = useState("");
  const [importing, setImporting] = useState(false);
  const [report, setReport] = useState(null);

  const knownSlugs = new Set((course?.modules || []).map((m) => m.slug));

  const parse = () => {
    setParseErr(""); setParsed(null);
    const t = text.trim();
    if (!t) { setParseErr("Paste JSON or CSV first."); return; }
    let items = null;
    if (t.startsWith("{") || t.startsWith("[")) {
      try {
        const obj = JSON.parse(t);
        const norm = normalizeItems(obj);
        if (norm.error) { setParseErr(norm.error); return; }
        items = norm.items;
      } catch (e) { setParseErr("JSON parse error: " + e.message); return; }
    } else {
      try {
        const rows = parseCsv(t);
        const norm = normalizeItems(rows);
        if (norm.error) { setParseErr(norm.error); return; }
        items = norm.items;
      } catch (e) { setParseErr("CSV parse error: " + e.message); return; }
    }
    if (!items || items.length === 0) { setParseErr("No items detected."); return; }
    const byModule = {};
    const unknown = [];
    items.forEach((it) => {
      byModule[it.module_slug] = (byModule[it.module_slug] || 0) + 1;
      if (!knownSlugs.has(it.module_slug)) unknown.push(it.module_slug);
    });
    setParsed({ items, byModule, unknown: Array.from(new Set(unknown)) });
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const t = await f.text();
    setText(t);
    setTimeout(parse, 0);
  };

  const doImport = async () => {
    if (!parsed?.items?.length) return;
    setImporting(true);
    try {
      const r = await adminApi.bulkImport(courseId, parsed.items);
      setReport(r);
      toast.success(`Imported ${r.inserted} question${r.inserted === 1 ? "" : "s"} · ${r.skipped} skipped`);
    } catch (e) { toast.error(e.message || "Import failed"); }
    finally { setImporting(false); }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 105,
      display: "grid", placeItems: "center", padding: 20, overflow: "auto",
    }} data-testid="import-bank-panel">
      <div style={{
        background: "#0a1413", border: "1px solid #2a403c", borderRadius: 6,
        maxWidth: 780, width: "100%", maxHeight: "92vh", overflow: "auto", padding: 24,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div className="tbg-section-sub" style={{ color: "#6db8ff" }}>
              <Upload size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              Bulk import · Question bank
            </div>
            <h2 style={{ margin: "4px 0 0", color: "#e2ece9", font: "700 16px 'Montserrat','Geist','Inter'" }}>
              Human-authored, awaiting spot-check
            </h2>
            <div style={{ color: "#7b8b87", fontSize: 12, marginTop: 6, lineHeight: 1.55 }}>
              Every question you import is flagged <b style={{ color: "#6db8ff" }}>AUTHOR PENDING</b> and
              stays out of student quiz &amp; exam pools until you approve it (per-question
              or one-click <b>Approve all</b> per module).
            </div>
          </div>
          <button className="tbg-btn ghost" style={{ padding: "5px 10px" }} onClick={onClose}
            data-testid="import-bank-close">
            <X size={12} /> Close
          </button>
        </div>

        {!report && (
          <>
            <div style={{ display: "flex", gap: 10, marginBottom: 8, alignItems: "center" }}>
              <label style={{
                display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
                padding: "6px 12px", border: "1px solid #2a403c", borderRadius: 4,
                color: "#e2ece9", font: "600 11px 'JetBrains Mono', monospace", letterSpacing: "0.14em",
              }}>
                <Upload size={12} /> Choose CSV or JSON file
                <input type="file" accept=".csv,.json,text/csv,application/json"
                  onChange={onFile} style={{ display: "none" }}
                  data-testid="import-bank-file" />
              </label>
              <span style={{ color: "#7b8b87", fontSize: 11 }}>or paste below</span>
            </div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onBlur={() => text.trim() && !parsed && parse()}
              placeholder='Paste JSON (either [{...}] or {"items":[...]}) OR CSV with headers: module_slug,prompt,option_a,option_b,option_c,option_d,correct_index,rationale,learning_objective_ref…'
              data-testid="import-bank-text"
              style={{
                width: "100%", minHeight: 220,
                background: "#050807", border: "1px solid #1c2a27", borderRadius: 4,
                padding: "10px 12px", color: "#e2ece9",
                font: "500 11px 'JetBrains Mono', monospace",
              }}
            />
            {parseErr && (
              <div className="tbg-lms-err" data-testid="import-bank-err" style={{ marginTop: 8 }}>{parseErr}</div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, flexWrap: "wrap", gap: 8 }}>
              <button className="tbg-btn ghost" onClick={parse} data-testid="import-bank-parse">
                <RefreshCw size={12} /> Preview
              </button>
              <button className="tbg-btn" onClick={doImport}
                disabled={!parsed || parsed.unknown.length > 0 || importing}
                data-testid="import-bank-submit"
                style={{ background: "#6db8ff", color: "#04121f" }}>
                <Upload size={13} /> {importing ? "Importing…" : `Import ${parsed?.items?.length || 0} question${parsed?.items?.length === 1 ? "" : "s"}`}
              </button>
            </div>

            {parsed && (
              <div style={{ marginTop: 14, padding: 12, background: "#08110f",
                border: "1px solid #1c2a27", borderRadius: 4 }} data-testid="import-bank-preview">
                <div style={{ font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.2em",
                  color: "#7b8b87", textTransform: "uppercase", marginBottom: 8 }}>
                  Preview · {parsed.items.length} question{parsed.items.length === 1 ? "" : "s"}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {Object.entries(parsed.byModule).map(([slug, n]) => {
                    const known = knownSlugs.has(slug);
                    return (
                      <span key={slug} style={{
                        color: known ? "#3dc0af" : "#ef6a6a",
                        padding: "3px 8px", border: `1px solid ${known ? "rgba(61,192,175,0.4)" : "rgba(239,106,106,0.4)"}`,
                        borderRadius: 3, font: "600 11px 'JetBrains Mono', monospace",
                      }}>
                        {slug}: {n}{!known && " · UNKNOWN"}
                      </span>
                    );
                  })}
                </div>
                {parsed.unknown.length > 0 && (
                  <div style={{ marginTop: 10, color: "#ef6a6a", fontSize: 12 }}>
                    Unknown module slug{parsed.unknown.length === 1 ? "" : "s"}: {parsed.unknown.join(", ")}. Fix or remove before importing.
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {report && (
          <div data-testid="import-bank-report">
            <div style={{ padding: "14px 16px", background: "rgba(61,192,175,0.08)",
              border: "1px solid rgba(61,192,175,0.4)", borderRadius: 4, color: "#3dc0af",
              fontSize: 13, lineHeight: 1.6 }}>
              <div style={{ font: "800 20px 'Montserrat','Geist','Inter'" }}>
                Imported {report.inserted} question{report.inserted === 1 ? "" : "s"}
              </div>
              <div style={{ color: "#a5b8b3", marginTop: 4 }}>
                {report.skipped > 0 && `${report.skipped} skipped total · `}
                All new questions are flagged <b>AUTHOR PENDING</b> and are held out of student pools.
              </div>
            </div>
            {(report.duplicates_skipped ?? 0) > 0 && (
              <div style={{ marginTop: 12, padding: "10px 12px",
                background: "rgba(240,190,60,0.08)", border: "1px solid rgba(240,190,60,0.4)",
                color: "#f0be3c", borderRadius: 4, fontSize: 12 }}
                data-testid="import-bank-duplicates">
                <b>{report.duplicates_skipped}</b> duplicate{report.duplicates_skipped === 1 ? "" : "s"} skipped
                (exact-prompt already exists in target module — safety guard against accidental
                double-imports).
                {report.duplicate_details && report.duplicate_details.length > 0 && (
                  <ul style={{ margin: "6px 0 0 18px", padding: 0, color: "#a5b8b3", fontSize: 11,
                    lineHeight: 1.55 }}>
                    {report.duplicate_details.slice(0, 6).map((d, i) => (
                      <li key={i}>
                        <span style={{ color: "#f0be3c" }}>{d.module_slug}</span>
                        {" · "}{d.prompt.slice(0, 90)}{d.prompt.length > 90 ? "…" : ""}
                      </li>
                    ))}
                    {report.duplicate_details.length > 6 && (
                      <li style={{ listStyle: "none", color: "#7b8b87" }}>
                        …and {report.duplicate_details.length - 6} more
                      </li>
                    )}
                  </ul>
                )}
              </div>
            )}
            <div style={{ marginTop: 12, display: "flex", flexWrap: "wrap", gap: 6 }}>
              {Object.entries(report.per_module).map(([slug, n]) => (
                <span key={slug} style={{
                  color: "#6db8ff", padding: "3px 8px",
                  border: "1px solid rgba(109,184,255,0.4)", borderRadius: 3,
                  font: "600 11px 'JetBrains Mono', monospace",
                }}>{slug}: {n}</span>
              ))}
            </div>
            {report.errors?.length > 0 && (
              <div style={{ marginTop: 12, padding: "10px 12px", background: "rgba(239,106,106,0.08)",
                border: "1px solid rgba(239,106,106,0.35)", color: "#ef6a6a",
                borderRadius: 4, fontSize: 12 }}>
                <b>Errors:</b>
                <ul style={{ margin: "6px 0 0 18px" }}>
                  {report.errors.map((e, i) => <li key={i}>{e}</li>)}
                </ul>
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16, gap: 8, flexWrap: "wrap" }}>
              <button className="tbg-btn ghost" onClick={() => { setReport(null); setText(""); setParsed(null); }}
                data-testid="import-bank-again">
                <Upload size={13} /> Import another batch
              </button>
              <button className="tbg-btn" onClick={onImported} data-testid="import-bank-done"
                style={{ background: "#3dc0af" }}>
                <CheckCircle2 size={13} /> Done · Refresh view
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* -------------------------- Export bank -------------------------- */

export function ExportBankPanel({ courseId, course, onClose }) {
  const [downloading, setDownloading] = useState(null); // "json" | "csv" | null
  const total = course?._exam_pool_total ?? 0;
  const approved = course?._exam_pool_real ?? 0;
  const authorPending = course?._exam_pool_author_pending ?? 0;
  const aiDrafts = course?._exam_pool_ai_drafted ?? 0;
  // Prefer the authoritative server-computed placeholder count; fall back to
  // the derived formula (clamped) for backwards compatibility.
  const placeholder = course?._exam_pool_placeholders ??
    Math.max(0, total - approved - authorPending - aiDrafts);

  const download = async (format) => {
    setDownloading(format);
    try {
      const token = getAdminToken();
      const res = await fetch(
        `${API}/lms/admin/courses/${courseId}/questions/export?format=${format}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const disp = res.headers.get("content-disposition") || "";
      const m = /filename="([^"]+)"/.exec(disp);
      const filename = m ? m[1] : `question-bank.${format}`;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) { toast.error(e.message || "Export failed"); }
    finally { setDownloading(null); }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 105,
      display: "grid", placeItems: "center", padding: 20, overflow: "auto",
    }} data-testid="export-bank-panel">
      <div style={{
        background: "#0a1413", border: "1px solid #2a403c", borderRadius: 6,
        maxWidth: 640, width: "100%", padding: 24,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div className="tbg-section-sub" style={{ color: "#3dc0af" }}>
              <Download size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              Export bank · Full snapshot
            </div>
            <h2 style={{ margin: "4px 0 0", color: "#e2ece9", font: "700 16px 'Montserrat','Geist','Inter'" }}>
              Backup every question with state labels
            </h2>
            <div style={{ color: "#7b8b87", fontSize: 12, marginTop: 6, lineHeight: 1.55 }}>
              Dumps <b style={{ color: "#e2ece9" }}>every</b> question in the bank — approved,
              author-pending, AI-drafted, and placeholder — each tagged with a plain-text
              <code style={{ color: "#3dc0af", background: "#050807", padding: "1px 4px", margin: "0 3px", borderRadius: 3 }}>state</code>
              field. Safe to commit to git or hand to a reviewer.
            </div>
          </div>
          <button className="tbg-btn ghost" style={{ padding: "5px 10px" }} onClick={onClose}
            data-testid="export-bank-close">
            <X size={12} /> Close
          </button>
        </div>

        <div style={{ padding: 14, background: "#08110f", border: "1px solid #1c2a27",
          borderRadius: 4, marginBottom: 16 }}>
          <div style={{ font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.2em",
            color: "#7b8b87", textTransform: "uppercase", marginBottom: 8 }}>
            What's in the export
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8,
            font: "500 11px 'JetBrains Mono', monospace" }} data-testid="export-bank-counts">
            <div><span style={{ color: "#3dc0af" }}>●</span> approved: <b style={{ color: "#e2ece9" }}>{approved}</b></div>
            <div><span style={{ color: "#6db8ff" }}>●</span> author_pending_review: <b style={{ color: "#e2ece9" }}>{authorPending}</b></div>
            <div><span style={{ color: "#9c6cf8" }}>●</span> ai_drafted_unreviewed: <b style={{ color: "#e2ece9" }}>{aiDrafts}</b></div>
            <div><span style={{ color: "#f0be3c" }}>●</span> placeholder: <b style={{ color: "#e2ece9" }}>{placeholder}</b></div>
          </div>
          <div style={{ marginTop: 10, color: "#7b8b87", fontSize: 11, lineHeight: 1.55 }}>
            Total: <b style={{ color: "#e2ece9" }}>{total}</b> question{total === 1 ? "" : "s"}
          </div>
        </div>

        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          <button className="tbg-btn" onClick={() => download("json")} disabled={downloading != null}
            data-testid="export-bank-json"
            style={{ background: "#3dc0af", color: "#04121f", flex: 1 }}>
            <Download size={13} /> {downloading === "json" ? "Preparing…" : "Download JSON"}
          </button>
          <button className="tbg-btn" onClick={() => download("csv")} disabled={downloading != null}
            data-testid="export-bank-csv"
            style={{ background: "#3dc0af", color: "#04121f", flex: 1 }}>
            <Download size={13} /> {downloading === "csv" ? "Preparing…" : "Download CSV"}
          </button>
        </div>
        <div style={{ marginTop: 14, padding: "10px 12px", background: "rgba(61,192,175,0.06)",
          border: "1px solid rgba(61,192,175,0.3)", borderRadius: 4, color: "#a5b8b3",
          fontSize: 11, lineHeight: 1.6 }}>
          <b style={{ color: "#3dc0af" }}>JSON</b> preserves full metadata: schema version, per-state
          definitions, options as an array, timestamps. Recommended for version-history / re-import.
          <br />
          <b style={{ color: "#3dc0af" }}>CSV</b> flattens options to <code>option_a…option_d</code>
          for spreadsheet review, and matches the import CSV format.
        </div>
      </div>
    </div>
  );
}

/* -------------------------- Round-trip check -------------------------- */

// Normalize incoming reference rows (from user's local CSV/JSON) into a common
// shape so we can compare against the current bank.
function normalizeReference(raw) {
  const arr = Array.isArray(raw) ? raw : Array.isArray(raw?.items) ? raw.items : null;
  if (!arr) return { rows: null, error: "Expected a JSON array or {items:[…]}." };
  const rows = [];
  for (let i = 0; i < arr.length; i++) {
    const r = arr[i];
    const options = Array.isArray(r.options) ? r.options
      : [r.option_a, r.option_b, r.option_c, r.option_d, r.option_e, r.option_f]
          .filter((x) => x !== undefined && x !== "");
    const ci = r.correct_index !== undefined && r.correct_index !== "" ? parseInt(r.correct_index, 10) : null;
    if (!r.module_slug || !r.prompt) {
      return { rows: null, error: `Row ${i + 1}: missing module_slug or prompt.` };
    }
    rows.push({
      id: r.id || null,
      module_slug: String(r.module_slug).trim(),
      prompt: String(r.prompt).trim(),
      options: options.map((o) => String(o)),
      correct_index: Number.isNaN(ci) ? null : ci,
      rationale: (r.rationale ? String(r.rationale) : "").trim(),
      learning_objective_ref: r.learning_objective_ref ? String(r.learning_objective_ref).trim() : null,
      source_question_number: r.source_question_number != null && r.source_question_number !== ""
        ? parseInt(r.source_question_number, 10) : null,
    });
  }
  return { rows, error: null };
}

// Diff `ref[]` against `db[]`. Match strategy:
//   1. exact prompt match (case-insensitive, trimmed) is authoritative
//   2. otherwise pair by (module_slug + source_question_number) if both sides have it
// Returns categorized change lists.
function diffBanks(ref, db) {
  const norm = (s) => (s || "").toLowerCase().trim().replace(/\s+/g, " ");
  const optsSig = (opts) => (opts || []).map((o) => norm(o)).join(" | ");

  const dbByPrompt = new Map();
  const dbByKey = new Map();  // module_slug + source_question_number
  for (const q of db) {
    dbByPrompt.set(norm(q.prompt), q);
    if (q.source_question_number != null) {
      dbByKey.set(`${q.module_slug}#${q.source_question_number}`, q);
    }
  }
  const refByPrompt = new Map();
  const refByKey = new Map();
  for (const q of ref) {
    refByPrompt.set(norm(q.prompt), q);
    if (q.source_question_number != null) {
      refByKey.set(`${q.module_slug}#${q.source_question_number}`, q);
    }
  }

  const findMatch = (side, other, byPrompt, byKey) => {
    const p = byPrompt.get(norm(side.prompt));
    if (p) return { match: p, how: "prompt" };
    if (side.source_question_number != null) {
      const k = byKey.get(`${side.module_slug}#${side.source_question_number}`);
      if (k) return { match: k, how: "source_question_number" };
    }
    return null;
  };

  const changes = {
    unchanged: [],
    reassignedModule: [],
    changedLo: [],
    changedContent: [],   // prompt-matched but options/rationale/correct_index differ
    editedPrompt: [],     // matched by source_question_number, prompt text differs
    onlyInRef: [],        // in your reference but not in the DB (deleted / renamed)
    onlyInDb: [],         // in the DB but not in your reference (added since export)
  };

  const seenDbIds = new Set();
  for (const rq of ref) {
    const m = findMatch(rq, db, dbByPrompt, dbByKey);
    if (!m) { changes.onlyInRef.push(rq); continue; }
    seenDbIds.add(m.match.prompt);
    const dq = m.match;
    const moved = rq.module_slug !== dq.module_slug;
    const loChanged = (rq.learning_objective_ref || "") !== (dq.learning_objective_ref || "");
    const optsChanged = optsSig(rq.options) !== optsSig(dq.options);
    const ratChanged = norm(rq.rationale) !== norm(dq.rationale);
    const ciChanged = rq.correct_index !== null && dq.correct_index !== null && rq.correct_index !== dq.correct_index;
    const promptChanged = norm(rq.prompt) !== norm(dq.prompt);  // only possible if matched via source_question_number

    const entry = { ref: rq, db: dq, how: m.how };
    if (moved) changes.reassignedModule.push({ ...entry, from: rq.module_slug, to: dq.module_slug });
    if (loChanged) changes.changedLo.push({ ...entry, ref_lo: rq.learning_objective_ref, db_lo: dq.learning_objective_ref });
    if (promptChanged && m.how === "source_question_number") changes.editedPrompt.push(entry);
    if ((optsChanged || ratChanged || ciChanged) && !moved && !promptChanged) {
      changes.changedContent.push({ ...entry, opts_changed: optsChanged, rat_changed: ratChanged, ci_changed: ciChanged });
    }
    if (!moved && !loChanged && !optsChanged && !ratChanged && !ciChanged && !promptChanged) {
      changes.unchanged.push(entry);
    }
  }
  const seenPrompts = new Set(ref.map((r) => norm(r.prompt)));
  const seenKeys = new Set(ref.filter((r) => r.source_question_number != null).map((r) => `${r.module_slug}#${r.source_question_number}`));
  for (const dq of db) {
    const inRef = seenPrompts.has(norm(dq.prompt))
      || (dq.source_question_number != null && seenKeys.has(`${dq.module_slug}#${dq.source_question_number}`));
    if (!inRef) changes.onlyInDb.push(dq);
  }
  return changes;
}

export function RoundTripCheckPanel({ courseId, onClose }) {
  const [text, setText] = useState("");
  const [refRows, setRefRows] = useState(null);
  const [parseErr, setParseErr] = useState("");
  const [refFileName, setRefFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const parse = (raw) => {
    setParseErr(""); setRefRows(null); setReport(null);
    const t = (raw ?? text).trim();
    if (!t) { setParseErr("Paste JSON/CSV or upload a file first."); return; }
    let items = null;
    if (t.startsWith("{") || t.startsWith("[")) {
      try {
        const obj = JSON.parse(t);
        const n = normalizeReference(obj);
        if (n.error) { setParseErr(n.error); return; }
        items = n.rows;
      } catch (e) { setParseErr("JSON parse error: " + e.message); return; }
    } else {
      try {
        const rows = parseCsv(t);
        const n = normalizeReference(rows);
        if (n.error) { setParseErr(n.error); return; }
        items = n.rows;
      } catch (e) { setParseErr("CSV parse error: " + e.message); return; }
    }
    if (!items || items.length === 0) { setParseErr("No items detected."); return; }
    setRefRows(items);
    toast.info(`Reference loaded — ${items.length} question${items.length === 1 ? "" : "s"}. Click Run comparison.`);
  };

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setRefFileName(f.name);
    const t = await f.text();
    setText(t);
    setTimeout(() => parse(t), 0);
  };

  const run = async () => {
    if (!refRows) { toast.error("Load a reference file first."); return; }
    setBusy(true);
    try {
      // Pull current DB bank via the same export endpoint the Export panel uses.
      // Filter to source-lineage rows only so we compare like-with-like against
      // typical reference files (which are the import CSVs).
      const token = getAdminToken();
      const res = await fetch(
        `${API}/lms/admin/courses/${courseId}/questions/export?format=json&source_only=true`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`Export fetch failed (${res.status})`);
      const payload = await res.json();
      const dbRows = (payload.items || []).map((it) => ({
        id: it.id,
        module_slug: it.module_slug,
        prompt: it.prompt,
        options: it.options,
        correct_index: it.correct_index,
        rationale: it.rationale || "",
        learning_objective_ref: it.learning_objective_ref || null,
        source_question_number: it.source_question_number != null ? it.source_question_number : null,
        state: it.state,
      }));
      setReport({ diff: diffBanks(refRows, dbRows), refTotal: refRows.length, dbTotal: dbRows.length });
    } catch (e) { toast.error(e.message || "Comparison failed"); }
    finally { setBusy(false); }
  };

  const total = refRows?.length || 0;
  const anyDrift = report && (
    report.diff.reassignedModule.length + report.diff.changedLo.length +
    report.diff.changedContent.length + report.diff.editedPrompt.length +
    report.diff.onlyInRef.length + report.diff.onlyInDb.length
  ) > 0;

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 105,
      display: "grid", placeItems: "center", padding: 20, overflow: "auto",
    }} data-testid="round-trip-panel">
      <div style={{
        background: "#0a1413", border: "1px solid #2a403c", borderRadius: 6,
        maxWidth: 880, width: "100%", maxHeight: "92vh", overflow: "auto", padding: 24,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div className="tbg-section-sub" style={{ color: "#f0be3c" }}>
              <GitCompare size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              Round-trip check
            </div>
            <h2 style={{ margin: "4px 0 0", color: "#e2ece9", font: "700 16px 'Montserrat','Geist','Inter'" }}>
              Does your reference file still match the DB?
            </h2>
            <div style={{ color: "#7b8b87", fontSize: 12, marginTop: 6, lineHeight: 1.55 }}>
              Upload your local reference (import CSV or a previous export) and this compares
              it row-by-row to the current bank. Highlights <b style={{ color: "#c9a8ff" }}>module
              reassignments</b>, <b style={{ color: "#6db8ff" }}>LO changes</b>, <b style={{ color: "#f0be3c" }}>content
              edits</b>, and rows on <b style={{ color: "#ef6a6a" }}>only one side</b>. Matches by
              prompt text first, falling back to <code style={{ color: "#3dc0af" }}>module_slug + source_question_number</code>.
            </div>
          </div>
          <button className="tbg-btn ghost" style={{ padding: "5px 10px" }} onClick={onClose}
            data-testid="round-trip-close">
            <X size={12} /> Close
          </button>
        </div>

        {!report && (
          <>
            <div style={{ display: "flex", gap: 10, marginBottom: 8, alignItems: "center", flexWrap: "wrap" }}>
              <label style={{
                display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
                padding: "6px 12px", border: "1px solid #2a403c", borderRadius: 4,
                color: "#e2ece9", font: "600 11px 'JetBrains Mono', monospace", letterSpacing: "0.14em",
              }}>
                <Upload size={12} /> Choose reference file
                <input type="file" accept=".csv,.json,text/csv,application/json"
                  onChange={onFile} style={{ display: "none" }}
                  data-testid="round-trip-file" />
              </label>
              {refFileName && (
                <span style={{ color: "#7b8b87", fontSize: 11 }}>loaded: <b style={{ color: "#e2ece9" }}>{refFileName}</b></span>
              )}
              {refRows && (
                <span style={{ color: "#3dc0af", fontSize: 11, marginLeft: "auto" }}>
                  ✓ {refRows.length} rows parsed
                </span>
              )}
            </div>
            <textarea
              value={text}
              onChange={(e) => setText(e.target.value)}
              onBlur={() => text.trim() && !refRows && parse()}
              placeholder="…or paste JSON / CSV directly."
              data-testid="round-trip-text"
              style={{
                width: "100%", minHeight: 160,
                background: "#050807", border: "1px solid #1c2a27", borderRadius: 4,
                padding: "10px 12px", color: "#e2ece9",
                font: "500 11px 'JetBrains Mono', monospace",
              }}
            />
            {parseErr && (
              <div className="tbg-lms-err" data-testid="round-trip-err" style={{ marginTop: 8 }}>{parseErr}</div>
            )}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 10, gap: 8 }}>
              <button className="tbg-btn ghost" onClick={() => parse()} data-testid="round-trip-parse">
                <RefreshCw size={12} /> Re-parse
              </button>
              <button className="tbg-btn" onClick={run} disabled={!refRows || busy}
                data-testid="round-trip-run"
                style={{ background: "#f0be3c", color: "#04121f" }}>
                <GitCompare size={13} /> {busy ? "Comparing…" : `Run comparison${total ? ` (${total} rows)` : ""}`}
              </button>
            </div>
          </>
        )}

        {report && (
          <div data-testid="round-trip-report">
            <DiffHeader report={report} anyDrift={anyDrift} />
            <DiffSection title="Module reassigned" color="#c9a8ff" testId="diff-reassigned-module"
              rows={report.diff.reassignedModule}
              render={(x) => (
                <>
                  <span style={{ color: "#ef6a6a" }}>{x.from}</span>
                  {" → "}
                  <span style={{ color: "#3dc0af" }}>{x.to}</span>
                  {" · "}
                  <span style={{ color: "#7b8b87" }}>{x.ref.prompt.slice(0, 90)}{x.ref.prompt.length > 90 ? "…" : ""}</span>
                </>
              )} />
            <DiffSection title="Learning objective changed" color="#6db8ff" testId="diff-changed-lo"
              rows={report.diff.changedLo}
              render={(x) => (
                <>
                  <div style={{ color: "#e2ece9" }}>{x.ref.prompt.slice(0, 90)}{x.ref.prompt.length > 90 ? "…" : ""}</div>
                  <div style={{ color: "#7b8b87", fontSize: 11, marginTop: 2 }}>
                    ref: <em style={{ color: "#ef6a6a" }}>{x.ref_lo || "(none)"}</em>
                    {" → "}db: <em style={{ color: "#3dc0af" }}>{x.db_lo || "(none)"}</em>
                  </div>
                </>
              )} />
            <DiffSection title="Content edited (options / rationale / correct index)" color="#f0be3c" testId="diff-changed-content"
              rows={report.diff.changedContent}
              render={(x) => (
                <>
                  <div style={{ color: "#e2ece9" }}>{x.ref.prompt.slice(0, 90)}{x.ref.prompt.length > 90 ? "…" : ""}</div>
                  <div style={{ color: "#7b8b87", fontSize: 11, marginTop: 2 }}>
                    {[x.opts_changed && "options", x.rat_changed && "rationale", x.ci_changed && "correct_index"]
                      .filter(Boolean).join(" · ")}
                  </div>
                </>
              )} />
            <DiffSection title="Prompt text edited (matched by source_question_number)" color="#f0be3c" testId="diff-edited-prompt"
              rows={report.diff.editedPrompt}
              render={(x) => (
                <>
                  <div style={{ color: "#7b8b87", fontSize: 11 }}>
                    <b style={{ color: "#ef6a6a" }}>ref:</b> {x.ref.prompt.slice(0, 130)}{x.ref.prompt.length > 130 ? "…" : ""}
                  </div>
                  <div style={{ color: "#7b8b87", fontSize: 11, marginTop: 2 }}>
                    <b style={{ color: "#3dc0af" }}>db:</b> {x.db.prompt.slice(0, 130)}{x.db.prompt.length > 130 ? "…" : ""}
                  </div>
                </>
              )} />
            <DiffSection title="Only in your reference (deleted or renamed in DB)" color="#ef6a6a" testId="diff-only-in-ref"
              rows={report.diff.onlyInRef}
              render={(x) => (
                <>
                  <span style={{ color: "#7b8b87" }}>{x.module_slug}</span>
                  {" · "}
                  <span style={{ color: "#e2ece9" }}>{x.prompt.slice(0, 90)}{x.prompt.length > 90 ? "…" : ""}</span>
                </>
              )} />
            <DiffSection title="Only in current DB (added since your reference)" color="#3dc0af" testId="diff-only-in-db"
              rows={report.diff.onlyInDb}
              render={(x) => (
                <>
                  <span style={{ color: "#7b8b87" }}>{x.module_slug}</span>
                  {" · "}
                  <span style={{ color: "#e2ece9" }}>{x.prompt.slice(0, 90)}{x.prompt.length > 90 ? "…" : ""}</span>
                </>
              )} />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 16, gap: 8, flexWrap: "wrap" }}>
              <button className="tbg-btn ghost" onClick={() => { setReport(null); setText(""); setRefRows(null); setRefFileName(""); }}
                data-testid="round-trip-new">
                <Upload size={13} /> Compare another file
              </button>
              <button className="tbg-btn" onClick={onClose} data-testid="round-trip-done"
                style={{ background: "#3dc0af" }}>
                <CheckCircle2 size={13} /> Done
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DiffHeader({ report, anyDrift }) {
  const d = report.diff;
  return (
    <div style={{ padding: "14px 16px", background: anyDrift ? "rgba(240,190,60,0.06)" : "rgba(61,192,175,0.08)",
      border: `1px solid ${anyDrift ? "rgba(240,190,60,0.4)" : "rgba(61,192,175,0.4)"}`, borderRadius: 4,
      marginBottom: 14 }} data-testid="round-trip-header">
      <div style={{ font: "800 20px 'Montserrat','Geist','Inter'", color: anyDrift ? "#f0be3c" : "#3dc0af" }}>
        {anyDrift ? "Drift detected" : "In sync"}
      </div>
      <div style={{ color: "#a5b8b3", marginTop: 4, fontSize: 12, lineHeight: 1.55 }}>
        Reference: <b style={{ color: "#e2ece9" }}>{report.refTotal}</b> rows ·
        &nbsp;DB: <b style={{ color: "#e2ece9" }}>{report.dbTotal}</b> rows ·
        &nbsp;matched unchanged: <b style={{ color: "#3dc0af" }}>{d.unchanged.length}</b>
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 10,
        font: "500 10px 'JetBrains Mono', monospace" }}>
        <Chip color="#c9a8ff" label="module reassigned" n={d.reassignedModule.length} />
        <Chip color="#6db8ff" label="LO changed" n={d.changedLo.length} />
        <Chip color="#f0be3c" label="content edited" n={d.changedContent.length} />
        <Chip color="#f0be3c" label="prompt edited" n={d.editedPrompt.length} />
        <Chip color="#ef6a6a" label="only in ref" n={d.onlyInRef.length} />
        <Chip color="#3dc0af" label="only in DB" n={d.onlyInDb.length} />
      </div>
    </div>
  );
}

function Chip({ color, label, n }) {
  return (
    <span style={{
      color: n > 0 ? color : "#5a6a67",
      padding: "3px 8px", border: `1px solid ${n > 0 ? color + "55" : "#1c2a27"}`, borderRadius: 3,
    }}>{n} {label}</span>
  );
}

function DiffSection({ title, color, testId, rows, render }) {
  if (!rows || rows.length === 0) return null;
  return (
    <div data-testid={testId} style={{ marginBottom: 12,
      padding: "12px 14px", background: "#0d1817",
      border: `1px solid ${color}33`, borderRadius: 4 }}>
      <div style={{ font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.16em",
        color, textTransform: "uppercase", marginBottom: 8 }}>
        {title} · {rows.length}
      </div>
      {rows.map((r, i) => (
        <div key={i} style={{
          padding: "6px 0", borderTop: i > 0 ? "1px solid rgba(255,255,255,0.03)" : 0,
          fontSize: 12, lineHeight: 1.5, color: "#a5b8b3",
        }}>
          {render(r)}
        </div>
      ))}
    </div>
  );
}


/* -------------------------- Course-wide gap analysis -------------------------- */

export function CourseGapPanel({ courseId, onClose }) {
  const [includePending, setIncludePending] = useState(true);
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const [modules, setModules] = useState([]);
  const [err, setErr] = useState("");

  const [course, setCourse] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const c = await adminApi.courseAuthoring(courseId);
        setCourse(c);
      } catch (e) { setErr(e.message || "Course load failed"); }
    })();
  }, [courseId]);

  const run = async () => {
    if (!course) return;
    setRunning(true); setErr("");
    const targets = (course.modules || []).filter((m) => !m.is_bonus && (m.learning_objectives || []).length > 0);
    setModules(targets.map((m) => ({ module_code: m.code, module_title: m.title, module_id: m.id })));
    setProgress({ done: 0, total: targets.length });

    // Cap concurrent Claude calls to 3 to sidestep edge/LLM concurrency 502s.
    // Each module gets one automatic retry after 2s backoff if the first
    // attempt errors — flaky-per-module failures were the earlier pattern.
    const MAX_CONCURRENT = 3;
    const RETRY_BACKOFF_MS = 2000;
    const outcomes = new Array(targets.length);

    const runOne = async (i) => {
      const m = targets[i];
      const attempt = async () => adminApi.coverageGapAnalysis(courseId, m.id, includePending);
      try {
        outcomes[i] = { status: "fulfilled", value: await attempt() };
      } catch (err1) {
        await new Promise((res) => setTimeout(res, RETRY_BACKOFF_MS));
        try {
          outcomes[i] = { status: "fulfilled", value: await attempt(), retried: true };
        } catch (err2) {
          outcomes[i] = { status: "rejected", reason: err2, retried: true };
        }
      } finally {
        setProgress((p) => ({ ...p, done: p.done + 1 }));
      }
    };

    // Simple concurrency-limited runner
    let cursor = 0;
    const workers = Array.from({ length: Math.min(MAX_CONCURRENT, targets.length) }, async () => {
      while (cursor < targets.length) {
        const idx = cursor++;
        await runOne(idx);
      }
    });
    await Promise.all(workers);

    const merged = targets.map((m, i) => {
      const r = outcomes[i];
      if (r.status === "fulfilled") {
        return { module_code: m.code, module_title: m.title, module_id: m.id, report: r.value, retried: !!r.retried };
      }
      return { module_code: m.code, module_title: m.title, module_id: m.id, error: (r.reason && r.reason.message) || "Failed", retried: !!r.retried };
    });
    setModules(merged);
    setRunning(false);
  };

  const statusColor = (s) => (s === "adequate" ? "#3dc0af" : s === "thin" ? "#f0be3c" : "#ef6a6a");
  const hasResults = modules.some((m) => m.report || m.error);
  const totalReviewable = modules.reduce((sum, m) => sum + (m.report?.approved_question_count || 0), 0);

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 105,
      display: "grid", placeItems: "center", padding: 20, overflow: "auto",
    }} data-testid="course-gap-panel">
      <div style={{
        background: "#0a1413", border: "1px solid #2a403c", borderRadius: 6,
        maxWidth: 880, width: "100%", maxHeight: "92vh", overflow: "auto", padding: 24,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div className="tbg-section-sub" style={{ color: "#9c6cf8" }}>
              <ListChecks size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              Course-wide coverage gap
            </div>
            <h2 style={{ margin: "4px 0 0", color: "#e2ece9", font: "700 16px 'Montserrat','Geist','Inter'" }}>
              LO coverage across every module
            </h2>
            <div style={{ color: "#7b8b87", fontSize: 12, marginTop: 4, lineHeight: 1.55 }}>
              Claude Sonnet 4.6 semantically matches each module's question pool against its
              learning objectives. Caps at 3 concurrent Claude calls with a single retry per
              module — repeat runs against the same bank return instantly from cache.
            </div>
          </div>
          <button className="tbg-btn ghost" style={{ padding: "5px 10px" }} onClick={onClose}
            data-testid="course-gap-close">
            <X size={12} /> Close
          </button>
        </div>

        <div style={{
          padding: "10px 12px", background: includePending ? "rgba(109,184,255,0.08)" : "#0d1817",
          border: `1px solid ${includePending ? "rgba(109,184,255,0.4)" : "#1c2a27"}`, borderRadius: 4,
          marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10,
        }}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", margin: 0,
            color: includePending ? "#a9d2f4" : "#a5b8b3", fontSize: 12 }}>
            <input type="checkbox" checked={includePending} onChange={(e) => setIncludePending(e.target.checked)}
              disabled={running}
              style={{ accentColor: "#6db8ff" }} data-testid="course-gap-include-pending" />
            Include <b style={{ color: includePending ? "#6db8ff" : "#a5b8b3" }}>author-pending</b> questions
            (recommended right after a bulk import)
          </label>
          <button className="tbg-btn" onClick={run} disabled={running || !course}
            data-testid="course-gap-run"
            style={{ background: "#9c6cf8" }}>
            <Sparkles size={13} /> {running ? `Analyzing ${progress.done}/${progress.total}…` : hasResults ? "Re-run" : "Run analysis"}
          </button>
        </div>

        {running && (
          <div style={{ padding: 30, textAlign: "center", color: "#c9a8ff", fontSize: 13 }}>
            <Sparkles size={14} className="spin" style={{ marginRight: 6, verticalAlign: "-2px" }} />
            Analyzing modules · {progress.done}/{progress.total} complete (3 concurrent, auto-retry)
          </div>
        )}
        {err && <div className="tbg-lms-err" data-testid="course-gap-err">{err}</div>}

        {hasResults && (
          <>
            <div style={{ padding: "10px 12px", background: "#08110f",
              border: "1px solid #1c2a27", borderRadius: 4, marginBottom: 12,
              font: "500 11px 'JetBrains Mono', monospace", color: "#a5b8b3" }}>
              Analyzed <b style={{ color: "#e2ece9" }}>{modules.length}</b> module(s) ·
              &nbsp;<b style={{ color: "#e2ece9" }}>{totalReviewable}</b> reviewable questions considered ·
              &nbsp;pending inclusion: <b style={{ color: includePending ? "#6db8ff" : "#7b8b87" }}>
                {includePending ? "ON" : "OFF"}</b>
            </div>
            {modules.map((m, i) => {
              if (m.error) {
                return (
                  <div key={i} style={{ padding: "10px 12px", marginBottom: 10,
                    background: "rgba(239,106,106,0.05)", border: "1px solid rgba(239,106,106,0.4)", borderRadius: 4 }}>
                    <div style={{ color: "#e2ece9", fontWeight: 600 }}>Module {m.module_code} · {m.module_title}</div>
                    <div style={{ color: "#ef6a6a", fontSize: 12, marginTop: 4 }}>Analysis failed: {m.error}</div>
                  </div>
                );
              }
              if (!m.report) return null;
              const r = m.report;
              const buckets = { adequate: 0, thin: 0, uncovered: 0 };
              r.los.forEach((l) => { buckets[l.coverage_status] = (buckets[l.coverage_status] || 0) + 1; });
              return (
                <div key={i} data-testid={`course-gap-module-${m.module_code}`}
                  style={{ padding: "12px 14px", marginBottom: 10,
                    background: "#0d1817", border: "1px solid #1c2a27", borderRadius: 4 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 240 }}>
                      <div style={{ font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.16em",
                        color: "#7b8b87", textTransform: "uppercase" }}>
                        Module {m.module_code} · {r.approved_question_count} question{r.approved_question_count === 1 ? "" : "s"} in pool
                      </div>
                      <div style={{ color: "#e2ece9", fontSize: 14, marginTop: 2, fontWeight: 600 }}>
                        {r.module_title}
                        {m.retried && (
                          <span style={{ marginLeft: 8, font: "600 9px 'JetBrains Mono', monospace",
                            letterSpacing: "0.16em", color: "#f0be3c",
                            padding: "2px 6px", border: "1px solid rgba(240,190,60,0.4)", borderRadius: 3 }}
                            title="First attempt failed; succeeded on retry">
                            RETRIED
                          </span>
                        )}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ color: statusColor("adequate"), fontSize: 11,
                        padding: "3px 8px", border: `1px solid ${statusColor("adequate")}55`, borderRadius: 3 }}>
                        {buckets.adequate} adequate
                      </span>
                      <span style={{ color: statusColor("thin"), fontSize: 11,
                        padding: "3px 8px", border: `1px solid ${statusColor("thin")}55`, borderRadius: 3 }}>
                        {buckets.thin} thin
                      </span>
                      <span style={{ color: statusColor("uncovered"), fontSize: 11,
                        padding: "3px 8px", border: `1px solid ${statusColor("uncovered")}55`, borderRadius: 3 }}>
                        {buckets.uncovered} uncovered
                      </span>
                    </div>
                  </div>
                  {r.recommendation && (
                    <div style={{ marginTop: 8, padding: "8px 10px", background: "rgba(156,108,248,0.08)",
                      border: "1px solid rgba(156,108,248,0.3)", color: "#c9a8ff", borderRadius: 3,
                      fontSize: 12, lineHeight: 1.5 }}>
                      {r.recommendation}
                    </div>
                  )}
                  {r.warnings && r.warnings.length > 0 && (
                    <div style={{ marginTop: 6, padding: "6px 10px", background: "rgba(240,190,60,0.06)",
                      border: "1px solid rgba(240,190,60,0.3)", color: "#f0be3c", borderRadius: 3, fontSize: 11 }}>
                      {r.warnings.join(" · ")}
                    </div>
                  )}
                </div>
              );
            })}
            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
              <button className="tbg-btn" onClick={onClose} data-testid="course-gap-done">Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}


/* MissingDiagramsPanel — scan every lesson body for `![…](asset:slug)` tokens
   and cross-check against the uploaded asset store. Flags:
   • Missing refs (student would see a broken image → P1)
   • Orphaned assets (uploaded, never referenced → cleanup opportunity)
*/
export function MissingDiagramsPanel({ courseId, onClose }) {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    setLoading(true); setErr("");
    try {
      const r = await adminApi.courseMissingDiagrams(courseId);
      setReport(r);
    } catch (e) { setErr(e.message || "Load failed"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [courseId]);

  const missing = report?.missing_refs || [];
  const orphaned = report?.orphaned_assets || [];
  const clean = !loading && !err && missing.length === 0 && orphaned.length === 0;

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 105,
      display: "grid", placeItems: "center", padding: 20, overflow: "auto",
    }} data-testid="missing-diagrams-panel">
      <div style={{
        background: "#0a1413", border: "1px solid #2a403c", borderRadius: 6,
        maxWidth: 900, width: "100%", maxHeight: "92vh", overflow: "auto", padding: 24,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div className="tbg-section-sub" style={{ color: "#ef6a6a" }}>
              <ImageIcon size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              Diagram integrity check
            </div>
            <h2 style={{ margin: "4px 0 0", color: "#e2ece9", font: "700 16px 'Montserrat','Geist','Inter'" }}>
              Missing & orphaned diagrams
            </h2>
            <div style={{ color: "#7b8b87", fontSize: 12, marginTop: 4, lineHeight: 1.55 }}>
              Scans every lesson body for <code style={{ color: "#c9a8ff" }}>![…](asset:slug)</code> tokens
              and cross-checks against the uploaded asset store. Run before publishing.
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="tbg-btn ghost" onClick={load} disabled={loading}
              data-testid="missing-diagrams-refresh">
              <RefreshCw size={12} className={loading ? "spin" : ""} /> Refresh
            </button>
            <button className="tbg-btn ghost" style={{ padding: "5px 10px" }} onClick={onClose}
              data-testid="missing-diagrams-close">
              <X size={12} /> Close
            </button>
          </div>
        </div>

        {loading && (
          <div style={{ padding: 30, textAlign: "center", color: "#7b8b87", fontSize: 13 }}>
            Scanning lesson bodies…
          </div>
        )}
        {err && <div className="tbg-lms-err" data-testid="missing-diagrams-err">{err}</div>}

        {!loading && !err && (
          <>
            <div style={{ padding: "10px 12px", background: "#08110f",
              border: "1px solid #1c2a27", borderRadius: 4, marginBottom: 12,
              font: "500 11px 'JetBrains Mono', monospace", color: "#a5b8b3" }}
              data-testid="missing-diagrams-summary">
              Scanned <b style={{ color: "#e2ece9" }}>{report?.lessons_scanned ?? 0}</b> lesson(s) ·
              &nbsp;missing refs:&nbsp;<b style={{ color: missing.length ? "#ef6a6a" : "#3dc0af" }}>{missing.length}</b> ·
              &nbsp;orphaned assets:&nbsp;<b style={{ color: orphaned.length ? "#f0be3c" : "#3dc0af" }}>{orphaned.length}</b>
            </div>

            {clean && (
              <div style={{ padding: "22px 16px", background: "rgba(61,192,175,0.06)",
                border: "1px solid rgba(61,192,175,0.4)", borderRadius: 4, color: "#3dc0af",
                textAlign: "center", fontSize: 14, fontWeight: 600 }}
                data-testid="missing-diagrams-clean">
                <CheckCircle2 size={16} style={{ verticalAlign: "-3px", marginRight: 6 }} />
                All diagram references resolved. Nothing orphaned.
              </div>
            )}

            {missing.length > 0 && (
              <div style={{ marginBottom: 14 }}>
                <div style={{ font: "700 11px 'JetBrains Mono', monospace", letterSpacing: "0.14em",
                  color: "#ef6a6a", textTransform: "uppercase", marginBottom: 8,
                  display: "flex", alignItems: "center", gap: 6 }}>
                  <AlertTriangle size={12} /> Missing refs ({missing.length}) — students will see broken images
                </div>
                {missing.map((m, i) => (
                  <div key={i} data-testid={`missing-diagrams-missing-${i}`}
                    style={{ padding: "10px 12px", marginBottom: 6, background: "rgba(239,106,106,0.05)",
                      border: "1px solid rgba(239,106,106,0.4)", borderRadius: 4 }}>
                    <div style={{ color: "#e2ece9", fontSize: 13 }}>
                      <b>{m.module_title}</b> · Lesson <span style={{ color: "#a5b8b3" }}>{m.lesson_title}</span>
                    </div>
                    <div style={{ marginTop: 4, font: "600 12px 'JetBrains Mono', monospace", color: "#ef6a6a" }}>
                      references <code>asset:{m.referenced_slug}</code> — no such upload
                    </div>
                  </div>
                ))}
              </div>
            )}

            {orphaned.length > 0 && (
              <div>
                <div style={{ font: "700 11px 'JetBrains Mono', monospace", letterSpacing: "0.14em",
                  color: "#f0be3c", textTransform: "uppercase", marginBottom: 8,
                  display: "flex", alignItems: "center", gap: 6 }}>
                  <AlertTriangle size={12} /> Orphaned assets ({orphaned.length}) — uploaded but not used
                </div>
                {orphaned.map((o, i) => (
                  <div key={i} data-testid={`missing-diagrams-orphaned-${i}`}
                    style={{ padding: "10px 12px", marginBottom: 6, background: "rgba(240,190,60,0.05)",
                      border: "1px solid rgba(240,190,60,0.3)", borderRadius: 4 }}>
                    <div style={{ color: "#e2ece9", fontSize: 13 }}>
                      <b>{o.module_title}</b> · Lesson <span style={{ color: "#a5b8b3" }}>{o.lesson_title}</span>
                    </div>
                    <div style={{ marginTop: 4, font: "600 12px 'JetBrains Mono', monospace", color: "#f0be3c" }}>
                      <code>{o.slug}</code> — uploaded but no <code>![…](asset:{o.slug})</code> token in body
                    </div>
                  </div>
                ))}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
              <button className="tbg-btn" onClick={onClose} data-testid="missing-diagrams-done">Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}



/* =========================================================================
   ContentSyncPanel — cross-environment bundle export + import.

   Design (Feb 2026):
   - "Push to Production" without needing direct network access between
     preview and prod (preview pod is IP-blocked from prod Atlas).
   - Two-step, safer flow: admin exports a JSON bundle on the SOURCE env,
     switches to the TARGET env, and re-uploads it with an explicit
     "overwrite" confirmation checkbox.
   - Upsert-by-`id` semantics on the receiver side; nothing is deleted.
   ========================================================================= */

const SYNC_COLLECTIONS = [
  { key: "questions", label: "Questions (quiz_questions)" },
  { key: "assets", label: "Lesson assets (SVG diagrams)" },
  { key: "courses", label: "Courses & lessons (courses)" },
];

export function ContentSyncPanel({ onClose }) {
  const [tab, setTab] = useState("export"); // "export" | "import"
  const [selectedExport, setSelectedExport] = useState({
    questions: true, assets: true, courses: false,
  });
  const [exporting, setExporting] = useState(false);

  const [importFile, setImportFile] = useState(null);
  const [importPreview, setImportPreview] = useState(null); // {schema_version, counts}
  const [selectedImport, setSelectedImport] = useState({
    questions: true, assets: true, courses: true,
  });
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [previewErr, setPreviewErr] = useState("");

  const toggleExport = (k) =>
    setSelectedExport((prev) => ({ ...prev, [k]: !prev[k] }));
  const toggleImport = (k) =>
    setSelectedImport((prev) => ({ ...prev, [k]: !prev[k] }));

  const download = async () => {
    const include = SYNC_COLLECTIONS.filter((c) => selectedExport[c.key]).map((c) => c.key);
    if (!include.length) {
      toast.error("Pick at least one collection to export.");
      return;
    }
    setExporting(true);
    try {
      const token = getAdminToken();
      const res = await fetch(
        `${API}/lms/admin/content/export?include=${include.join(",")}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) throw new Error(`Export failed (${res.status})`);
      const disp = res.headers.get("content-disposition") || "";
      const m = /filename="([^"]+)"/.exec(disp);
      const filename = m ? m[1] : `tbg-content-bundle.json`;
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      toast.error(e.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  const onFilePicked = async (file) => {
    setImportFile(file);
    setImportPreview(null);
    setImportResult(null);
    setPreviewErr("");
    if (!file) return;
    try {
      const text = await file.text();
      const parsed = JSON.parse(text);
      if (!parsed || typeof parsed !== "object" || !parsed.schema_version) {
        throw new Error("Not a valid TBG content bundle (missing schema_version).");
      }
      const preview = {
        schema_version: parsed.schema_version,
        exported_at: parsed.exported_at,
        counts: parsed.counts || {},
        available: {
          questions: Array.isArray(parsed.questions),
          assets: Array.isArray(parsed.assets),
          courses: Array.isArray(parsed.courses),
        },
        _raw: parsed,
      };
      // Auto-check only collections that are actually present in the bundle.
      setSelectedImport({
        questions: preview.available.questions,
        assets: preview.available.assets,
        courses: preview.available.courses,
      });
      setImportPreview(preview);
    } catch (e) {
      setPreviewErr(e.message || "Could not parse the file as JSON.");
    }
  };

  const runImport = async () => {
    if (!importPreview) return;
    if (!confirmOverwrite) {
      toast.error("Tick the overwrite checkbox to proceed.");
      return;
    }
    const include = SYNC_COLLECTIONS.filter((c) => selectedImport[c.key]).map((c) => c.key);
    if (!include.length) {
      toast.error("Pick at least one collection to import.");
      return;
    }
    setImporting(true);
    setImportResult(null);
    try {
      const token = getAdminToken();
      const res = await fetch(`${API}/lms/admin/content/import`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          bundle: importPreview._raw,
          confirm_overwrite: true,
          include,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.detail || `Import failed (${res.status})`);
      setImportResult(data);
      toast.success("Import complete.");
    } catch (e) {
      toast.error(e.message || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 105,
      display: "grid", placeItems: "center", padding: 20, overflow: "auto",
    }} data-testid="content-sync-panel">
      <div style={{
        background: "#0a1413", border: "1px solid #2a403c", borderRadius: 6,
        maxWidth: 720, width: "100%", padding: 24,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div className="tbg-section-sub" style={{ color: "#c9a8ff" }}>
              <Share2 size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              Content sync · Preview ↔ Production
            </div>
            <h2 style={{ margin: "4px 0 0", color: "#e2ece9", font: "700 16px 'Montserrat','Geist','Inter'" }}>
              Push authored content between environments
            </h2>
            <div style={{ color: "#7b8b87", fontSize: 12, marginTop: 6, lineHeight: 1.55 }}>
              Two-step, network-free flow. <b style={{ color: "#e2ece9" }}>Export</b> a JSON bundle from
              this environment → sign in on the other env → <b style={{ color: "#e2ece9" }}>Import</b>{" "}
              the same file. Upsert-by-<code style={{ color: "#c9a8ff", background: "#050807", padding: "1px 4px", margin: "0 2px", borderRadius: 3 }}>id</code>{" "}
              semantics; existing rows are updated in place, nothing is deleted.
            </div>
          </div>
          <button className="tbg-btn ghost" style={{ padding: "5px 10px" }} onClick={onClose}
            data-testid="content-sync-close">
            <X size={12} /> Close
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4, marginBottom: 16,
          borderBottom: "1px solid #1c2a27" }}>
          {[
            { k: "export", label: "Export bundle", icon: Download },
            { k: "import", label: "Import bundle", icon: Upload },
          ].map(({ k, label, icon: Icon }) => (
            <button key={k} onClick={() => setTab(k)}
              data-testid={`content-sync-tab-${k}`}
              style={{
                padding: "8px 14px", background: "transparent",
                border: "none", borderBottom: tab === k
                  ? "2px solid #c9a8ff" : "2px solid transparent",
                color: tab === k ? "#c9a8ff" : "#7b8b87",
                font: "700 11px 'JetBrains Mono', monospace",
                letterSpacing: "0.16em", textTransform: "uppercase",
                cursor: "pointer",
              }}>
              <Icon size={12} style={{ verticalAlign: "-2px", marginRight: 6 }} />
              {label}
            </button>
          ))}
        </div>

        {tab === "export" && (
          <div data-testid="content-sync-export">
            <div style={{ font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.2em",
              color: "#7b8b87", textTransform: "uppercase", marginBottom: 8 }}>
              Collections to include
            </div>
            <div style={{ display: "grid", gap: 6, marginBottom: 16 }}>
              {SYNC_COLLECTIONS.map((c) => (
                <label key={c.key} style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "8px 12px", background: "#08110f",
                  border: "1px solid #1c2a27", borderRadius: 4,
                  cursor: "pointer", font: "500 12px 'JetBrains Mono', monospace",
                  color: "#e2ece9",
                }}>
                  <input type="checkbox" checked={!!selectedExport[c.key]}
                    onChange={() => toggleExport(c.key)}
                    data-testid={`content-sync-export-${c.key}`}
                    style={{ width: 14, height: 14, cursor: "pointer" }} />
                  {c.label}
                </label>
              ))}
            </div>
            <button className="tbg-btn" onClick={download} disabled={exporting}
              data-testid="content-sync-download"
              style={{ borderColor: "rgba(201,168,255,0.6)", color: "#c9a8ff" }}>
              <Download size={12} /> {exporting ? "Preparing…" : "Download bundle"}
            </button>
            <div style={{ marginTop: 12, color: "#7b8b87", fontSize: 11, lineHeight: 1.55 }}>
              File is a plain JSON with <code style={{ color: "#c9a8ff", background: "#050807", padding: "1px 4px", borderRadius: 3 }}>schema_version</code>,
              per-collection counts, and no MongoDB <code style={{ color: "#c9a8ff", background: "#050807", padding: "1px 4px", borderRadius: 3 }}>_id</code> fields.
              Move it to the target environment however you like (VS Code download → local disk → target /admin upload).
            </div>
          </div>
        )}

        {tab === "import" && (
          <div data-testid="content-sync-import">
            <div style={{ font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.2em",
              color: "#7b8b87", textTransform: "uppercase", marginBottom: 8 }}>
              Pick a bundle file
            </div>
            <input type="file" accept="application/json,.json"
              onChange={(e) => onFilePicked(e.target.files?.[0])}
              data-testid="content-sync-file"
              style={{
                width: "100%", padding: 10, background: "#08110f",
                border: "1px solid #1c2a27", borderRadius: 4,
                color: "#e2ece9", font: "500 12px 'JetBrains Mono', monospace",
                marginBottom: 12,
              }} />
            {previewErr && (
              <div style={{
                padding: 10, background: "rgba(239,106,106,0.08)",
                border: "1px solid rgba(239,106,106,0.4)", borderRadius: 4,
                color: "#ef6a6a", font: "500 11px 'JetBrains Mono', monospace",
                marginBottom: 12,
              }} data-testid="content-sync-preview-error">
                <AlertTriangle size={11} style={{ verticalAlign: "-1px", marginRight: 4 }} />
                {previewErr}
              </div>
            )}
            {importPreview && (
              <>
                <div style={{
                  padding: 12, background: "#08110f",
                  border: "1px solid #1c2a27", borderRadius: 4,
                  marginBottom: 12, font: "500 11px 'JetBrains Mono', monospace",
                  color: "#e2ece9",
                }} data-testid="content-sync-preview">
                  <div style={{ color: "#7b8b87", marginBottom: 6, letterSpacing: "0.14em",
                    fontSize: 10, textTransform: "uppercase" }}>Bundle summary</div>
                  <div>schema_version: <b style={{ color: "#c9a8ff" }}>{importPreview.schema_version}</b></div>
                  {importPreview.exported_at && (
                    <div style={{ marginTop: 3 }}>exported_at: <span style={{ color: "#7b8b87" }}>{importPreview.exported_at}</span></div>
                  )}
                  <div style={{ marginTop: 6 }}>
                    counts: {" "}
                    {Object.entries(importPreview.counts).map(([k, v], i, arr) => (
                      <span key={k}>
                        <b style={{ color: "#c9a8ff" }}>{k}</b>=<b style={{ color: "#e2ece9" }}>{v}</b>
                        {i < arr.length - 1 ? ", " : ""}
                      </span>
                    ))}
                  </div>
                </div>

                <div style={{ font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.2em",
                  color: "#7b8b87", textTransform: "uppercase", marginBottom: 8 }}>
                  Collections to import
                </div>
                <div style={{ display: "grid", gap: 6, marginBottom: 12 }}>
                  {SYNC_COLLECTIONS.map((c) => {
                    const available = importPreview.available[c.key];
                    return (
                      <label key={c.key} style={{
                        display: "flex", alignItems: "center", gap: 8,
                        padding: "8px 12px", background: "#08110f",
                        border: "1px solid #1c2a27", borderRadius: 4,
                        cursor: available ? "pointer" : "not-allowed",
                        opacity: available ? 1 : 0.4,
                        font: "500 12px 'JetBrains Mono', monospace",
                        color: "#e2ece9",
                      }}>
                        <input type="checkbox"
                          disabled={!available}
                          checked={!!selectedImport[c.key] && available}
                          onChange={() => available && toggleImport(c.key)}
                          data-testid={`content-sync-import-${c.key}`}
                          style={{ width: 14, height: 14, cursor: available ? "pointer" : "not-allowed" }} />
                        {c.label}
                        {!available && (
                          <span style={{ marginLeft: "auto", color: "#7b8b87", fontSize: 10, letterSpacing: "0.14em" }}>
                            NOT IN BUNDLE
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>

                <label style={{
                  display: "flex", alignItems: "center", gap: 8,
                  padding: "10px 12px", background: "rgba(239,106,106,0.05)",
                  border: "1px solid rgba(239,106,106,0.4)", borderRadius: 4,
                  cursor: "pointer", marginBottom: 12,
                  font: "600 11px 'JetBrains Mono', monospace",
                  color: "#ef6a6a", letterSpacing: "0.06em",
                }}>
                  <input type="checkbox" checked={confirmOverwrite}
                    onChange={(e) => setConfirmOverwrite(e.target.checked)}
                    data-testid="content-sync-confirm-overwrite"
                    style={{ width: 14, height: 14, cursor: "pointer" }} />
                  Yes, upsert existing rows by <code style={{ background: "#050807", padding: "1px 4px", borderRadius: 3 }}>id</code>. Existing content will be overwritten.
                </label>

                <button className="tbg-btn" onClick={runImport}
                  disabled={importing || !confirmOverwrite}
                  data-testid="content-sync-run-import"
                  style={{ borderColor: "rgba(201,168,255,0.6)", color: "#c9a8ff" }}>
                  <Upload size={12} /> {importing ? "Importing…" : "Import bundle"}
                </button>
              </>
            )}

            {importResult && (
              <div style={{
                marginTop: 16, padding: 14, background: "#08110f",
                border: "1px solid #1c2a27", borderRadius: 4,
              }} data-testid="content-sync-result">
                <div style={{ font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.2em",
                  color: "#3dc0af", textTransform: "uppercase", marginBottom: 10 }}>
                  <CheckCircle2 size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />
                  Import result
                </div>
                {SYNC_COLLECTIONS.map((c) => {
                  const s = importResult[c.key];
                  if (!s) return null;
                  return (
                    <div key={c.key} style={{
                      marginBottom: 10, font: "500 12px 'JetBrains Mono', monospace",
                      color: "#e2ece9",
                    }} data-testid={`content-sync-result-${c.key}`}>
                      <b style={{ color: "#c9a8ff" }}>{c.key}</b>:{" "}
                      <span style={{ color: "#3dc0af" }}>{s.inserted} inserted</span> ·{" "}
                      <span style={{ color: "#6db8ff" }}>{s.updated} updated</span> ·{" "}
                      <span style={{ color: "#7b8b87" }}>{s.skipped} skipped</span>
                      {s.errors?.length > 0 && (
                        <ul style={{ margin: "6px 0 0 18px", color: "#ef6a6a", fontSize: 11 }}>
                          {s.errors.map((err, i) => <li key={i}>{err}</li>)}
                        </ul>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

/* =========================================================================
   BuyerSimPanel — QA helper for the post-payment onboarding flow.

   Two tabs:
   - "Simulate purchase": fires POST /api/payments/admin/simulate-purchase
     to create a student + tx row and return a magic-link URL. Used to
     walk the full onboarding flow (welcome email → set password →
     portal → first lesson) without touching Stripe.
   - "Get magic link": fires POST /api/payments/admin/magic-link for an
     existing student. Returns a fresh 48h magic link without sending
     an email or creating any DB rows. Useful when a real buyer's
     welcome email got spam-filtered.

   Both flows finish with a big copyable URL + a one-click copy button.
   ========================================================================= */

export function BuyerSimPanel({ onClose }) {
  const [tab, setTab] = useState("simulate"); // "simulate" | "magic-link" | "resend-welcome" | "bulk"
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  const reset = () => { setResult(null); setErr(""); };

  const run = async () => {
    reset();
    if (!email.trim()) { setErr("Enter an email address."); return; }
    setBusy(true);
    try {
      const token = getAdminToken();
      let url;
      if (tab === "simulate") url = `${API}/payments/admin/simulate-purchase`;
      else if (tab === "resend-welcome") url = `${API}/payments/admin/resend-welcome`;
      else url = `${API}/payments/admin/magic-link`;
      const body = tab === "simulate"
        ? { email: email.trim(), name: name.trim() || undefined }
        : { email: email.trim() };
      const r = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data?.detail || `Request failed (${r.status})`);
      setResult(data);
      if (tab === "resend-welcome") {
        if (data.email_sent) toast.success(`Welcome email resent (id ${data.message_id?.slice(0, 8) || "ok"}).`);
        else toast.error(`Email send failed: ${data.error || "unknown"}`);
      }
    } catch (e) {
      setErr(e.message || "Request failed");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async () => {
    if (!result?.magic_link_url) return;
    try {
      await navigator.clipboard.writeText(result.magic_link_url);
      toast.success("Magic link copied to clipboard.");
    } catch {
      // Fallback for very old browsers / non-secure origins
      const ta = document.createElement("textarea");
      ta.value = result.magic_link_url;
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); toast.success("Copied."); }
      catch { toast.error("Copy failed — select the URL manually below."); }
      document.body.removeChild(ta);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 105,
      display: "grid", placeItems: "center", padding: 20, overflow: "auto",
    }} data-testid="buyer-sim-panel">
      <div style={{
        background: "#0a1413", border: "1px solid #2a403c", borderRadius: 6,
        maxWidth: 720, width: "100%", padding: 24,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div className="tbg-section-sub" style={{ color: "#3dc0af" }}>
              <UserPlus size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              Buyer simulator · QA & support
            </div>
            <h2 style={{ margin: "4px 0 0", color: "#e2ece9", font: "700 16px 'Montserrat','Geist','Inter'" }}>
              Simulate a purchase / resend a magic link
            </h2>
            <div style={{ color: "#7b8b87", fontSize: 12, marginTop: 6, lineHeight: 1.55 }}>
              <b style={{ color: "#e2ece9" }}>Simulate purchase</b> — creates a student + course
              access + sends the real welcome email, without touching Stripe. Every
              simulated payment is tagged <code style={{ color: "#3dc0af", background: "#050807", padding: "1px 4px", borderRadius: 3 }}>simulated=true</code> so it stays out of revenue reports.
              <br />
              <b style={{ color: "#e2ece9" }}>Get magic link</b> — for existing students only.
              Returns a fresh 48h URL without sending an email or modifying any data.
              <br />
              <b style={{ color: "#e2ece9" }}>Resend welcome</b> — for existing students who
              never got (or lost) the set-password email. Mints a fresh 48h link and
              actually ships the native welcome email via Resend. Returns the
              provider message id so you can confirm the send server-side.
            </div>
          </div>
          <button className="tbg-btn ghost" style={{ padding: "5px 10px" }} onClick={onClose}
            data-testid="buyer-sim-close">
            <X size={12} /> Close
          </button>
        </div>

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4, marginBottom: 16,
          borderBottom: "1px solid #1c2a27" }}>
          {[
            { k: "simulate", label: "Simulate purchase", icon: UserPlus },
            { k: "magic-link", label: "Get magic link", icon: Mail },
            { k: "resend-welcome", label: "Resend welcome", icon: Mail },
            { k: "bulk", label: "Bulk purchases", icon: UserPlus },
          ].map(({ k, label, icon: Icon }) => (
            <button key={k} onClick={() => { setTab(k); reset(); }}
              data-testid={`buyer-sim-tab-${k}`}
              style={{
                padding: "8px 14px", background: "transparent",
                border: "none", borderBottom: tab === k
                  ? "2px solid #3dc0af" : "2px solid transparent",
                color: tab === k ? "#3dc0af" : "#7b8b87",
                font: "700 11px 'JetBrains Mono', monospace",
                letterSpacing: "0.16em", textTransform: "uppercase",
                cursor: "pointer",
              }}>
              <Icon size={12} style={{ verticalAlign: "-2px", marginRight: 6 }} />
              {label}
            </button>
          ))}
        </div>

        {tab === "bulk" ? (
          <BulkPurchasesPanel />
        ) : (<>
        <div style={{ display: "grid", gap: 10, marginBottom: 14 }}>
          <label style={{ font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.2em",
            color: "#7b8b87", textTransform: "uppercase" }}>
            Buyer email
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com" autoFocus
              data-testid="buyer-sim-email"
              style={{
                display: "block", width: "100%", marginTop: 6,
                padding: 10, background: "#08110f",
                border: "1px solid #1c2a27", borderRadius: 4,
                color: "#e2ece9", font: "500 13px 'JetBrains Mono', monospace",
                textTransform: "none", letterSpacing: 0,
              }} />
          </label>
          {tab === "simulate" && (
            <label style={{ font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.2em",
              color: "#7b8b87", textTransform: "uppercase" }}>
              Buyer name (optional)
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                placeholder="Test Buyer"
                data-testid="buyer-sim-name"
                style={{
                  display: "block", width: "100%", marginTop: 6,
                  padding: 10, background: "#08110f",
                  border: "1px solid #1c2a27", borderRadius: 4,
                  color: "#e2ece9", font: "500 13px 'JetBrains Mono', monospace",
                  textTransform: "none", letterSpacing: 0,
                }} />
            </label>
          )}
        </div>

        <button className="tbg-btn" onClick={run} disabled={busy || !email.trim()}
          data-testid="buyer-sim-run">
          {tab === "simulate" && (<><UserPlus size={14} /> {busy ? "Simulating…" : "Simulate purchase"}</>)}
          {tab === "magic-link" && (<><Mail size={14} /> {busy ? "Fetching…" : "Get magic link"}</>)}
          {tab === "resend-welcome" && (<><Mail size={14} /> {busy ? "Resending…" : "Resend welcome email"}</>)}
        </button>

        {err && (
          <div style={{
            marginTop: 14, padding: 10, background: "rgba(239,106,106,0.08)",
            border: "1px solid rgba(239,106,106,0.4)", borderRadius: 4,
            color: "#ef6a6a", font: "500 11px 'JetBrains Mono', monospace",
          }} data-testid="buyer-sim-error">
            <AlertTriangle size={11} style={{ verticalAlign: "-1px", marginRight: 4 }} />
            {err}
          </div>
        )}

        {result && (
          <div style={{
            marginTop: 16, padding: 14, background: "#08110f",
            border: "1px solid #1c2a27", borderRadius: 4,
          }} data-testid="buyer-sim-result">
            <div style={{ font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.2em",
              color: "#3dc0af", textTransform: "uppercase", marginBottom: 10 }}>
              <CheckCircle2 size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              {tab === "simulate" && "Purchase simulated"}
              {tab === "magic-link" && "Magic link ready"}
              {tab === "resend-welcome" && (result.email_sent ? "Welcome email resent" : "Resend attempted — see error")}
            </div>
            <div style={{ font: "500 12px 'JetBrains Mono', monospace", color: "#e2ece9",
              display: "grid", gap: 4, marginBottom: 12 }}>
              <div>student_id: <span style={{ color: "#7b8b87" }}>{result.student_id}</span></div>
              <div>email: <span style={{ color: "#7b8b87" }}>{result.email}</span></div>
              {"name" in result && result.name && (
                <div>name: <span style={{ color: "#7b8b87" }}>{result.name}</span></div>
              )}
              {"course_id" in result && (
                <div>course: <span style={{ color: "#c9a8ff" }}>{result.course_id}</span></div>
              )}
              {Array.isArray(result.course_ids) && (
                <div>courses: <span style={{ color: "#c9a8ff" }}>{result.course_ids.join(", ")}</span></div>
              )}
              {"reused_existing_student" in result && (
                <div>reused_existing_student: <span style={{ color: result.reused_existing_student ? "#c9a8ff" : "#3dc0af" }}>
                  {String(result.reused_existing_student)}
                </span></div>
              )}
              {"email_sent" in result && (
                <div>welcome_email_sent: <span style={{ color: result.email_sent ? "#3dc0af" : "#ef6a6a" }}>
                  {String(result.email_sent)}
                </span></div>
              )}
              {result.message_id && (
                <div>message_id: <span style={{ color: "#7b8b87" }} data-testid="buyer-sim-message-id">{result.message_id}</span></div>
              )}
              {result.error && (
                <div style={{ color: "#ef6a6a" }} data-testid="buyer-sim-send-error">
                  error: {result.error}{result.status_code ? ` (HTTP ${result.status_code})` : ""}
                </div>
              )}
            </div>

            <div style={{ font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.2em",
              color: "#7b8b87", textTransform: "uppercase", marginBottom: 6 }}>
              Magic link URL (valid 48h)
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "stretch" }}>
              <input readOnly value={result.magic_link_url}
                data-testid="buyer-sim-magic-link"
                onFocus={(e) => e.target.select()}
                style={{
                  flex: 1, padding: 10, background: "#050807",
                  border: "1px solid #1c2a27", borderRadius: 4,
                  color: "#e2ece9", font: "500 11px 'JetBrains Mono', monospace",
                }} />
              <button className="tbg-btn" onClick={copyLink}
                data-testid="buyer-sim-copy"
                style={{ borderColor: "rgba(61,192,175,0.6)", color: "#3dc0af",
                  whiteSpace: "nowrap" }}>
                <Copy size={12} /> Copy
              </button>
              <a href={result.magic_link_url} target="_blank" rel="noreferrer"
                data-testid="buyer-sim-open"
                className="tbg-btn"
                style={{ borderColor: "rgba(201,168,255,0.6)", color: "#c9a8ff",
                  textDecoration: "none", whiteSpace: "nowrap",
                  display: "inline-flex", alignItems: "center", gap: 6 }}>
                Open ↗
              </a>
            </div>
          </div>
        )}
        </>
        )}
      </div>
    </div>
  );
}



/* =========================================================================
   AccountingPanel — CRA tax remittance report (Feb 2026).

   Downloads a ZIP with transactions.csv + summary.csv + refunds.csv for
   the selected date range. The bundle is what a bookkeeper attaches to
   the quarterly GST/HST return (per-jurisdiction breakdown of tax
   collected, gross sales, and refunds issued).

   The date pickers default to the *previous* calendar quarter — that's
   the window operators actually reach for once a filing deadline hits.
   ========================================================================= */

/* Compute the previous calendar quarter's date range. Runs at panel-open
   time so the user gets a sane default without having to fiddle. */
function _previousQuarterRange(now = new Date()) {
  const y = now.getFullYear();
  const m = now.getMonth(); // 0-11
  const q = Math.floor(m / 3); // 0..3 = current quarter
  const prevQIndex = q === 0 ? 3 : q - 1;
  const prevQYear = q === 0 ? y - 1 : y;
  const startMonth = prevQIndex * 3;                // 0, 3, 6, 9
  const endMonth = startMonth + 2;                  // 2, 5, 8, 11
  const start = new Date(Date.UTC(prevQYear, startMonth, 1));
  // Day 0 of the next month = last day of the desired month.
  const end = new Date(Date.UTC(prevQYear, endMonth + 1, 0));
  const iso = (d) => d.toISOString().slice(0, 10);
  return { start: iso(start), end: iso(end), label: `${prevQYear}Q${prevQIndex + 1}` };
}

export function AccountingPanel({ onClose }) {
  const initial = _previousQuarterRange();
  const [start, setStart] = useState(initial.start);
  const [end, setEnd] = useState(initial.end);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [lastFilename, setLastFilename] = useState("");

  const applyPreset = (which) => {
    const now = new Date();
    if (which === "prev-quarter") {
      const r = _previousQuarterRange(now);
      setStart(r.start); setEnd(r.end); setErr("");
    } else if (which === "ytd") {
      const y = now.getFullYear();
      setStart(`${y}-01-01`);
      setEnd(now.toISOString().slice(0, 10));
      setErr("");
    } else if (which === "last-12m") {
      const past = new Date(now); past.setUTCFullYear(past.getUTCFullYear() - 1);
      setStart(past.toISOString().slice(0, 10));
      setEnd(now.toISOString().slice(0, 10));
      setErr("");
    }
  };

  const download = async () => {
    setErr("");
    if (!start || !end) { setErr("Pick a start and end date."); return; }
    if (end < start) { setErr("End date must be on or after start date."); return; }
    setBusy(true);
    try {
      const token = getAdminToken();
      const url = `${API}/payments/admin/reports/tax-remittance?start=${start}&end=${end}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) {
        let msg = `Report failed (${res.status})`;
        try { const j = await res.json(); if (j?.detail) msg = j.detail; } catch { /* noop */ }
        throw new Error(msg);
      }
      const disp = res.headers.get("content-disposition") || "";
      const m = /filename="([^"]+)"/.exec(disp);
      const filename = m ? m[1] : `TGI-CRA-Remittance-${start}_to_${end}.zip`;
      const blob = await res.blob();
      const bloburl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = bloburl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(bloburl);
      setLastFilename(filename);
      toast.success(`Downloaded ${filename}`);
    } catch (e) {
      setErr(e.message || "Report failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 105,
      display: "grid", placeItems: "center", padding: 20, overflow: "auto",
    }} data-testid="accounting-panel">
      <div style={{
        background: "#0a1413", border: "1px solid #2a403c", borderRadius: 6,
        maxWidth: 640, width: "100%", padding: 24,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div className="tbg-section-sub" style={{ color: "#f0be3c" }}>
              <FileText size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              Accounting · CRA remittance
            </div>
            <h2 style={{ margin: "4px 0 0", color: "#e2ece9", font: "700 16px 'Montserrat','Geist','Inter'" }}>
              Download tax remittance report
            </h2>
            <div style={{ color: "#7b8b87", fontSize: 12, marginTop: 6, lineHeight: 1.55 }}>
              Bundles three CSVs into a ZIP for the selected date range:{" "}
              <code style={{ color: "#f0be3c", background: "#050807", padding: "1px 4px", borderRadius: 3 }}>transactions.csv</code>,{" "}
              <code style={{ color: "#f0be3c", background: "#050807", padding: "1px 4px", borderRadius: 3 }}>summary.csv</code>,{" "}
              <code style={{ color: "#f0be3c", background: "#050807", padding: "1px 4px", borderRadius: 3 }}>refunds.csv</code>.
              Per-jurisdiction breakdown (GST/HST/QST/PST/RST). Simulated QA
              purchases are excluded automatically.
            </div>
          </div>
          <button className="tbg-btn ghost" style={{ padding: "5px 10px" }} onClick={onClose}
            data-testid="accounting-close">
            <X size={12} /> Close
          </button>
        </div>

        {/* Presets */}
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 14 }}>
          {[
            { k: "prev-quarter", label: "Previous quarter" },
            { k: "ytd", label: "Year-to-date" },
            { k: "last-12m", label: "Last 12 months" },
          ].map((p) => (
            <button key={p.k} className="tbg-btn ghost" onClick={() => applyPreset(p.k)}
              data-testid={`accounting-preset-${p.k}`}
              style={{ padding: "6px 12px", fontSize: 11 }}>
              {p.label}
            </button>
          ))}
        </div>

        {/* Date pickers */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
          <label style={{ font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.2em",
            color: "#7b8b87", textTransform: "uppercase" }}>
            Start date
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)}
              data-testid="accounting-start-date"
              style={{
                display: "block", width: "100%", marginTop: 6,
                padding: 10, background: "#08110f",
                border: "1px solid #1c2a27", borderRadius: 4,
                color: "#e2ece9", font: "500 13px 'JetBrains Mono', monospace",
                textTransform: "none", letterSpacing: 0,
              }} />
          </label>
          <label style={{ font: "700 10px 'JetBrains Mono', monospace", letterSpacing: "0.2em",
            color: "#7b8b87", textTransform: "uppercase" }}>
            End date (inclusive)
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)}
              data-testid="accounting-end-date"
              style={{
                display: "block", width: "100%", marginTop: 6,
                padding: 10, background: "#08110f",
                border: "1px solid #1c2a27", borderRadius: 4,
                color: "#e2ece9", font: "500 13px 'JetBrains Mono', monospace",
                textTransform: "none", letterSpacing: 0,
              }} />
          </label>
        </div>

        <button className="tbg-btn" onClick={download} disabled={busy}
          data-testid="accounting-download"
          style={{ borderColor: "rgba(240,190,60,0.6)", color: "#f0be3c" }}>
          <Download size={12} /> {busy ? "Generating…" : "Download report (.zip)"}
        </button>

        {err && (
          <div style={{
            marginTop: 14, padding: 10, background: "rgba(239,106,106,0.08)",
            border: "1px solid rgba(239,106,106,0.4)", borderRadius: 4,
            color: "#ef6a6a", font: "500 11px 'JetBrains Mono', monospace",
          }} data-testid="accounting-error">
            <AlertTriangle size={11} style={{ verticalAlign: "-1px", marginRight: 4 }} />
            {err}
          </div>
        )}

        {lastFilename && !err && (
          <div style={{
            marginTop: 14, padding: 10, background: "rgba(61,192,175,0.06)",
            border: "1px solid rgba(61,192,175,0.4)", borderRadius: 4,
            color: "#3dc0af", font: "500 11px 'JetBrains Mono', monospace",
          }} data-testid="accounting-last-filename">
            <CheckCircle2 size={11} style={{ verticalAlign: "-1px", marginRight: 4 }} />
            Downloaded: {lastFilename}
          </div>
        )}
      </div>
    </div>
  );
}


/* =========================================================================
   BulkPurchasesPanel — admin tool for OFFLINE-sold bulk / school licences.
   Lives inside BuyerSimPanel's 4th tab. Lets the admin:
     1. Record a new bulk sale (org name + contact + seats + price + payment
        reference). Backend auto-generates an 8-char School ID and emails
        the contact.
     2. See every licence with seats-used running count, status, and
        per-row actions: edit seats, add seats, deactivate/reactivate,
        resend-email, delete (guarded if any student is linked).
   Backed by /api/admin/school-licences/*.
   ========================================================================= */
function BulkPurchasesPanel() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);         // licence being edited
  const [form, setForm] = useState(_blankLicenceForm());

  const load = async () => {
    setLoading(true);
    try {
      const r = await _adminFetch(`${API}/admin/school-licences`);
      setRows(r.rows || []);
    } catch (e) {
      toast.error(e.message || "Failed to load licences");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.organization_name || !form.contact_name || !form.contact_email || !form.seats_purchased) {
      toast.error("Please fill in organization, contact, email, and seats.");
      return;
    }
    const body = {
      organization_name: form.organization_name.trim(),
      contact_name: form.contact_name.trim(),
      contact_email: form.contact_email.trim().toLowerCase(),
      seats_purchased: Number(form.seats_purchased),
      price_paid_cents: form.price_paid_dollars ? Math.round(Number(form.price_paid_dollars) * 100) : undefined,
      payment_reference: form.payment_reference || undefined,
      date_paid: form.date_paid || undefined,
      notes: form.notes || undefined,
    };
    try {
      if (editing) {
        await _adminFetch(`${API}/admin/school-licences/${editing.id}`, "PATCH", body);
        toast.success(`Updated ${body.organization_name}.`);
      } else {
        const r = await _adminFetch(`${API}/admin/school-licences`, "POST", body);
        toast.success(`School ID: ${r.school_id} — email sent to ${r.contact_email}.`);
      }
      setShowForm(false); setEditing(null); setForm(_blankLicenceForm());
      await load();
    } catch (e) {
      toast.error(e.message || "Save failed");
    }
  };

  const resend = async (lic) => {
    try {
      const r = await _adminFetch(`${API}/admin/school-licences/${lic.id}/resend-email`, "POST", {});
      if (r.ok) toast.success(`Email resent (id ${(r.message_id || "").slice(0, 8)}).`);
      else toast.error(`Resend failed: ${r.error}`);
    } catch (e) { toast.error(e.message); }
  };
  const toggleStatus = async (lic) => {
    const next = lic.status === "active" ? "inactive" : "active";
    try {
      await _adminFetch(`${API}/admin/school-licences/${lic.id}`, "PATCH", { status: next });
      await load();
    } catch (e) { toast.error(e.message); }
  };
  const addSeats = async (lic) => {
    const n = window.prompt(`Add how many seats to ${lic.organization_name}? (current ${lic.seats_purchased})`);
    const extra = Number(n);
    if (!extra || extra < 1) return;
    try {
      await _adminFetch(`${API}/admin/school-licences/${lic.id}`, "PATCH", { seats_purchased: lic.seats_purchased + extra });
      toast.success(`Added ${extra} seat(s).`);
      await load();
    } catch (e) { toast.error(e.message); }
  };
  const edit = (lic) => {
    setEditing(lic);
    setForm({
      organization_name: lic.organization_name,
      contact_name: lic.contact_name,
      contact_email: lic.contact_email,
      seats_purchased: lic.seats_purchased,
      price_paid_dollars: lic.price_paid_cents != null ? (lic.price_paid_cents / 100).toFixed(2) : "",
      payment_reference: lic.payment_reference || "",
      date_paid: lic.date_paid || "",
      notes: lic.notes || "",
    });
    setShowForm(true);
  };
  const del = async (lic) => {
    if (!window.confirm(`Delete licence for ${lic.organization_name}? This cannot be undone.`)) return;
    try {
      await _adminFetch(`${API}/admin/school-licences/${lic.id}`, "DELETE");
      toast.success("Deleted.");
      await load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <div data-testid="bulk-purchases-panel" style={{ display: "grid", gap: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ color: "#7b8b87", font: "500 11px 'JetBrains Mono', monospace" }}>
          Offline bulk / school licences. Each licence auto-emails a School ID
          to the contact, who shares it with their students for self-registration.
        </div>
        <button className="tbg-btn" onClick={() => { setEditing(null); setForm(_blankLicenceForm()); setShowForm(!showForm); }}
          data-testid="bulk-add-licence">
          {showForm ? "× Close form" : "+ New licence"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} data-testid="bulk-licence-form"
          style={{ display: "grid", gap: 10, padding: 14,
            background: "#091514", border: "1px solid #1c2a27", borderRadius: 4 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            {_formField("Organization / school name *", "organization_name", form, setForm, "test-school-name")}
            {_formField("Main contact name *", "contact_name", form, setForm, "test-school-contact")}
            {_formField("Contact email *", "contact_email", form, setForm, "test-school-email", "email")}
            {_formField("Seats purchased *", "seats_purchased", form, setForm, "test-school-seats", "number")}
            {_formField("Price paid (CAD, optional)", "price_paid_dollars", form, setForm, null, "number")}
            {_formField("Payment reference (optional)", "payment_reference", form, setForm)}
            {_formField("Date paid (optional)", "date_paid", form, setForm, null, "date")}
          </div>
          <label style={{ font: "700 10px 'JetBrains Mono', monospace",
            color: "#7b8b87", letterSpacing: "0.2em", textTransform: "uppercase" }}>
            Internal notes
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              style={{ display: "block", width: "100%", marginTop: 6, padding: 8,
                background: "#08110f", border: "1px solid #1c2a27",
                color: "#e2ece9", borderRadius: 4, minHeight: 60,
                font: "500 12px 'JetBrains Mono', monospace", textTransform: "none" }} />
          </label>
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button type="button" className="tbg-btn ghost"
              onClick={() => { setShowForm(false); setEditing(null); }}>Cancel</button>
            <button type="submit" className="tbg-btn" data-testid="bulk-licence-submit">
              {editing ? "Save changes" : "Create & send email"}
            </button>
          </div>
        </form>
      )}

      <div style={{ overflowX: "auto", border: "1px solid #1c2a27", borderRadius: 4 }}>
        <table style={{ width: "100%", borderCollapse: "collapse",
          font: "500 11px 'JetBrains Mono', monospace", color: "#e2ece9" }}>
          <thead>
            <tr>
              {["Organization", "School ID", "Seats", "Contact", "Status", "Actions"].map((h) => (
                <th key={h} style={{ background: "#0d1817", color: "#7b8b87",
                  textAlign: "left", padding: "9px 10px",
                  borderBottom: "1px solid #1c2a27",
                  letterSpacing: "0.2em", textTransform: "uppercase", fontSize: 10 }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && rows.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 16, color: "#7b8b87" }}>Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={6} style={{ padding: 16, color: "#7b8b87" }}>No bulk licences yet. Click "+ New licence" to create one.</td></tr>
            )}
            {rows.map((lic, i) => (
              <tr key={lic.id}
                style={{ borderBottom: "1px solid #11201d",
                  background: i % 2 ? "#070f0e" : "#091514",
                  opacity: lic.status === "inactive" ? 0.55 : 1 }}>
                <td style={{ padding: "8px 10px", color: "#e2ece9" }}>{lic.organization_name}</td>
                <td style={{ padding: "8px 10px", color: "#3dc0af", letterSpacing: "0.1em" }}>{lic.school_id}</td>
                <td style={{ padding: "8px 10px" }}>
                  <span style={{ color: lic.seats_used >= lic.seats_purchased ? "#f0be3c" : "#3dc0af" }}>
                    {lic.seats_used}
                  </span>
                  <span style={{ color: "#7b8b87" }}>/{lic.seats_purchased}</span>
                </td>
                <td style={{ padding: "8px 10px", color: "#a5b8b3" }}>{lic.contact_email}</td>
                <td style={{ padding: "8px 10px",
                  color: lic.status === "active" ? "#3dc0af" : "#7b8b87" }}>{lic.status}</td>
                <td style={{ padding: "6px 10px", display: "flex", flexWrap: "wrap", gap: 4 }}>
                  <RowBtn onClick={() => edit(lic)} label="Edit" />
                  <RowBtn onClick={() => addSeats(lic)} label="+ Seats" />
                  <RowBtn onClick={() => resend(lic)} label="Resend email" />
                  <RowBtn onClick={() => toggleStatus(lic)}
                    label={lic.status === "active" ? "Deactivate" : "Reactivate"} />
                  <RowBtn onClick={() => del(lic)} label="Delete" danger />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function _blankLicenceForm() {
  return {
    organization_name: "", contact_name: "", contact_email: "",
    seats_purchased: "", price_paid_dollars: "", payment_reference: "",
    date_paid: new Date().toISOString().slice(0, 10), notes: "",
  };
}
function _formField(label, key, form, setForm, testid, type = "text") {
  return (
    <label style={{ font: "700 10px 'JetBrains Mono', monospace",
      color: "#7b8b87", letterSpacing: "0.2em", textTransform: "uppercase" }}>
      {label}
      <input type={type} value={form[key]}
        {...(testid ? { "data-testid": testid } : {})}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
        style={{ display: "block", width: "100%", marginTop: 6, padding: 8,
          background: "#08110f", border: "1px solid #1c2a27",
          color: "#e2ece9", borderRadius: 4,
          font: "500 12px 'JetBrains Mono', monospace", textTransform: "none" }} />
    </label>
  );
}
function RowBtn({ onClick, label, danger }) {
  return (
    <button type="button" onClick={onClick}
      style={{ background: "transparent",
        border: "1px solid " + (danger ? "rgba(239,106,106,0.5)" : "#2a403c"),
        color: danger ? "#ef6a6a" : "#a5b8b3",
        padding: "3px 8px", borderRadius: 999, cursor: "pointer",
        font: "600 10px 'JetBrains Mono', monospace", letterSpacing: "0.08em" }}>
      {label}
    </button>
  );
}
async function _adminFetch(url, method = "GET", body) {
  const token = getAdminToken();
  const headers = { Authorization: `Bearer ${token}` };
  if (body) headers["Content-Type"] = "application/json";
  const r = await fetch(url, { method, headers, body: body ? JSON.stringify(body) : undefined });
  if (!r.ok) {
    const d = await r.json().catch(() => ({}));
    throw new Error(d.detail || `Request failed (${r.status})`);
  }
  // Return JSON if present; else undefined (e.g., 204).
  const ct = r.headers.get("content-type") || "";
  return ct.includes("json") ? r.json() : undefined;
}



/* ================================================================
   TtsWarmupPanel — pre-generate natural-voice narration for every
   lesson in the course. Perfect before a school launch: by the time
   the first student clicks Listen, every lesson plays instantly.

   • Shows live per-voice coverage (how many lessons are cached).
   • Admin can start a warmup for any subset of the 9 voices.
   • Progress ticks in real time while the background job runs.
   • Running jobs are deduped per course so refresh-mash doesn't
     spawn duplicate ECU burn.
   ================================================================ */
export function TtsWarmupPanel({ courseId, onClose }) {
  // Single voice we ship with — only this one shows in the picker so admin
  // doesn't accidentally warm voices the student will never hear.
  const WARMUP_VOICE = "am_adam";
  const [coverage, setCoverage] = useState(null);
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const [starting, setStarting] = useState(false);
  const [err, setErr] = useState("");

  // Initial load — coverage snapshot.
  useEffect(() => {
    (async () => {
      try {
        const cov = await adminApi.ttsWarmupCoverage(courseId);
        setCoverage(cov);
      } catch (e) {
        setErr(e.message || "Load failed");
      }
    })();
  }, [courseId]);

  // Poll the running job every 3 seconds.
  useEffect(() => {
    if (!jobId) return;
    let stop = false;
    const tick = async () => {
      try {
        const s = await adminApi.ttsWarmupStatus(jobId);
        if (stop) return;
        setJob(s);
        if (s.status === "done" || s.status === "failed") {
          // Final coverage refresh so the "cached" counts match reality.
          const cov = await adminApi.ttsWarmupCoverage(courseId);
          if (!stop) setCoverage(cov);
          return;
        }
        setTimeout(tick, 3000);
      } catch (e) {
        if (!stop) setErr(e.message || "Status check failed");
      }
    };
    tick();
    return () => { stop = true; };
  }, [jobId, courseId]);

  const start = async () => {
    setErr("");
    setStarting(true);
    try {
      const res = await adminApi.ttsWarmupStart(courseId, [WARMUP_VOICE]);
      setJobId(res.job_id);
      setJob(res);
    } catch (e) {
      setErr(e.message || "Could not start warmup");
    } finally {
      setStarting(false);
    }
  };

  const running = job?.status === "running" || job?.status === "queued";
  const voiceCov = coverage?.coverage?.[WARMUP_VOICE] || { total: 0, cached: 0 };
  const covPct = voiceCov.total ? Math.round((voiceCov.cached / voiceCov.total) * 100) : 0;

  return (
    <div style={{
      position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)", zIndex: 105,
      display: "grid", placeItems: "center", padding: 20, overflow: "auto",
    }} data-testid="tts-warmup-panel">
      <div style={{
        background: "#0a1413", border: "1px solid #2a403c", borderRadius: 6,
        maxWidth: 820, width: "100%", maxHeight: "92vh", overflow: "auto", padding: 24,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
          <div>
            <div className="tbg-section-sub" style={{ color: "#3dc0af" }}>
              <Headphones size={12} style={{ verticalAlign: "-2px", marginRight: 4 }} />
              Lesson narration cache
            </div>
            <h2 style={{ margin: "4px 0 0", color: "#e2ece9", font: "700 16px 'Montserrat','Geist','Inter'" }}>
              Pre-generate TTS audio
            </h2>
            <div style={{ color: "#7b8b87", fontSize: 12, marginTop: 4, lineHeight: 1.55 }}>
              Run this before a school launch. Every lesson × voice combination you tick
              will be generated in the background and cached so the first student who
              clicks Listen gets instant playback. Already-cached combos are skipped (free).
            </div>
          </div>
          <button className="tbg-btn ghost" style={{ padding: "5px 10px" }} onClick={onClose}
            data-testid="tts-warmup-close">
            <X size={12} /> Close
          </button>
        </div>

        {err && <div className="tbg-lms-err" data-testid="tts-warmup-err">{err}</div>}

        {!coverage && !err && (
          <div style={{ padding: 20, textAlign: "center", color: "#7b8b87", fontSize: 13 }}>
            Loading coverage…
          </div>
        )}

        {coverage && (
          <>
            <div style={{
              padding: "14px 16px", marginBottom: 14, borderRadius: 4,
              border: "1px solid #1c2a27", background: "#08110f",
              display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap",
            }} data-testid="tts-warmup-coverage">
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ font: "600 10px 'JetBrains Mono', monospace", letterSpacing: "0.14em",
                  color: "#3dc0af", textTransform: "uppercase", marginBottom: 4 }}>
                  Narrator — Adam (male, friendly)
                </div>
                <div style={{ color: "#e2ece9", font: "700 18px 'Montserrat'", letterSpacing: "-0.01em" }}>
                  {voiceCov.cached} / {voiceCov.total} lessons cached
                </div>
                <div style={{ marginTop: 8, height: 8, background: "#1c2a27", borderRadius: 999, overflow: "hidden" }}>
                  <div style={{
                    height: "100%", width: `${covPct}%`,
                    background: covPct === 100 ? "#3dc0af" : covPct > 0 ? "#f0be3c" : "#2a403c",
                    transition: "width 0.4s ease-out",
                  }} />
                </div>
              </div>
              <div style={{ color: "#7b8b87", fontSize: 11, letterSpacing: "0.04em", lineHeight: 1.5, maxWidth: 240 }}>
                {covPct === 100
                  ? "Every lesson is pre-cached. Students get instant playback on first click."
                  : covPct === 0
                    ? "No lessons cached yet. Warm the cache now so students don't wait on first listen."
                    : `${voiceCov.total - voiceCov.cached} lesson${voiceCov.total - voiceCov.cached === 1 ? "" : "s"} still need narration generated.`}
              </div>
            </div>

            {job && (
              <div style={{
                padding: "12px 14px", marginBottom: 14, borderRadius: 4,
                border: `1px solid ${job.status === "failed" ? "rgba(239,106,106,0.5)" : "rgba(61,192,175,0.4)"}`,
                background: job.status === "failed"
                  ? "rgba(239,106,106,0.05)" : "rgba(61,192,175,0.05)",
              }} data-testid="tts-warmup-progress">
                <div style={{ font: "600 10px 'JetBrains Mono', monospace",
                  color: job.status === "failed" ? "#ef6a6a" : "#3dc0af",
                  letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 4 }}>
                  {job.status === "done" ? "Warmup complete" :
                   job.status === "failed" ? "Warmup failed" :
                   "Warmup in progress"}
                </div>
                <div style={{ color: "#e2ece9", fontSize: 13 }}>
                  {job.completed}/{job.total} generated
                  {" · "}
                  <span style={{ color: "#7b8b87" }}>
                    {job.cache_hits} cache hits · {job.failed} failed
                  </span>
                </div>
                {job.total > 0 && (
                  <div style={{
                    marginTop: 8, height: 6, background: "#1c2a27", borderRadius: 999, overflow: "hidden",
                  }}>
                    <div style={{
                      height: "100%", width: `${(job.completed/job.total)*100}%`,
                      background: job.status === "failed" ? "#ef6a6a" : "#3dc0af",
                      transition: "width 0.5s ease-out",
                    }} />
                  </div>
                )}
                {job.errors && job.errors.length > 0 && (
                  <details style={{ marginTop: 8 }}>
                    <summary style={{ cursor: "pointer", color: "#f0be3c",
                      font: "500 11px 'JetBrains Mono', monospace" }}>
                      {job.errors.length} error{job.errors.length === 1 ? "" : "s"} — expand
                    </summary>
                    <div style={{ marginTop: 6, maxHeight: 200, overflow: "auto",
                      font: "500 10px 'JetBrains Mono', monospace", color: "#a5b8b3", lineHeight: 1.6 }}>
                      {job.errors.map((e, i) => <div key={i}>{e}</div>)}
                    </div>
                  </details>
                )}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
              <div style={{ color: "#7b8b87", fontSize: 12 }}>
                {covPct === 100
                  ? "Nothing to generate."
                  : `${voiceCov.total - voiceCov.cached} new clip${voiceCov.total - voiceCov.cached === 1 ? "" : "s"} · estimated 15–25 min`}
              </div>
              <button
                className="tbg-btn"
                onClick={start}
                disabled={running || starting || covPct === 100}
                data-testid="tts-warmup-start"
              >
                {starting ? "Starting…" : running ? "Running…" : covPct === 100 ? "All cached ✓" : "Start warmup"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// VoiceGroup component removed — the single-voice shipped build no
// longer needs a multi-voice picker.
